// receptorForm.ts
// Handles Step 1: receptor input, submission, and Mol* loading
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { prepareReceptor, checkReceptor, deleteSessionReceptor, getSessionDockings, getBindingSiteInfo, stopReceptor as stopReceptorAPI, getSessionScreenings, launchScreening as launchScreeningAPI, checkScreening, stopScreening, deleteSessionScreening } from '../backendClient';
import { updatePeptideDetailsGlobal } from './peptideForm';
// resetBindingSiteForm removed - no longer needed
// Global state to track deletion in progress
let isDeletingReceptor = false;
let deletionQueue = [];
// Global reference to update function
let globalUpdateReceptorDeleteButtons = null;
// Global state to prevent multiple simultaneous loading calls
let isLoadingReceptors = false;
// Binding site state per receptor (so switching receptor does not carry over selection)
function getBindingSiteState(receptorID) {
    const map = window.bindingSiteByReceptor;
    if (!map || !map[receptorID])
        return { bindingSiteID: null, customBindingSiteID: null, submitted: false };
    return map[receptorID];
}
function setBindingSiteState(receptorID, state) {
    if (!window.bindingSiteByReceptor)
        window.bindingSiteByReceptor = {};
    window.bindingSiteByReceptor[receptorID] = state;
}
// Check if current user has screening access (username ends with "-screening")
function hasScreeningAccess() {
    try {
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser)
            return false;
        const userData = JSON.parse(currentUser);
        const username = (userData === null || userData === void 0 ? void 0 : userData.username) || '';
        return username.endsWith('-screening');
    }
    catch (_a) {
        return false;
    }
}
// No renumbering needed - items are sorted by creation date from backend
/**
 * Initializes logic for receptor preparation (wizard step 1).
 */
export function initReceptorForm() {
    // DOM elements
    const input = document.getElementById('receptor-input');
    const submitBtn = document.getElementById('submit-receptor');
    // step2Btn removed - no longer needed with new tab structure
    const listContainer = ensureReceptorListContainer();
    // Expose updateReceptorDeleteButtons globally
    globalUpdateReceptorDeleteButtons = updateReceptorDeleteButtons;
    // Expose globally for dockingForm access
    window.updateReceptorDeleteButtons = updateReceptorDeleteButtons;
    window.loadSessionReceptors = loadSessionReceptors;
    // Function for centralized loading - calls the existing local function
    function loadSessionReceptors() {
        return __awaiter(this, void 0, void 0, function* () {
            // Prevent multiple simultaneous loading calls
            if (isLoadingReceptors) {
                return;
            }
            isLoadingReceptors = true;
            try {
                const { getSessionReceptors } = yield import('../backendClient');
                const data = yield getSessionReceptors();
                if ((data === null || data === void 0 ? void 0 : data.error) === 'rate_exceeded') {
                    const showToast = window.showToast;
                    if (showToast)
                        showToast('Too many requests. Please wait a moment and try again.', 'warning', 5000);
                    return;
                }
                const receptors = (data === null || data === void 0 ? void 0 : data.receptors) || [];
                if (receptors.length === 0) {
                    listContainer.innerHTML = '';
                    listContainer.style.display = 'none';
                    return;
                }
                // Clear existing receptors first to avoid duplicates
                listContainer.innerHTML = '';
                listContainer.style.display = 'block';
                // Get dockings and screenings to check which receptors are referenced (for hiding delete buttons)
                let referencedReceptors = new Set();
                let anyProcessing = false;
                try {
                    const { getSessionDockings, getSessionScreenings } = yield import('../backendClient');
                    const [dockingsData, screeningsData] = yield Promise.all([getSessionDockings(), getSessionScreenings()]);
                    const dockings = (dockingsData === null || dockingsData === void 0 ? void 0 : dockingsData.dockings) || [];
                    const screenings = (screeningsData === null || screeningsData === void 0 ? void 0 : screeningsData.screenings) || [];
                    dockings.forEach((dk) => {
                        if (dk && typeof dk.receptorID === 'number') {
                            referencedReceptors.add(dk.receptorID);
                        }
                        if (dk && (dk.status === 'pending' || dk.status === 'in_progress')) {
                            anyProcessing = true;
                        }
                    });
                    screenings.forEach((s) => {
                        var _a;
                        if (s && (typeof s.receptorID === 'number' || typeof s.receptorId === 'number')) {
                            referencedReceptors.add(Number((_a = s.receptorID) !== null && _a !== void 0 ? _a : s.receptorId));
                        }
                    });
                }
                catch (error) {
                    console.error('Error loading dockings/screenings for delete button check:', error);
                }
                // Check if any receptor is processing
                anyProcessing = anyProcessing || receptors.some(rec => rec.status === 'in_progress' || rec.status === 'pending');
                // Create receptor elements using the existing createReceptorElement function
                for (let index = 0; index < receptors.length; index++) {
                    const rec = receptors[index];
                    const receptorItem = {
                        receptorID: rec.receptorID,
                        pdbID: rec.pdbID,
                        receptorReady: rec.status === 'completed',
                        status: rec.status || 'pending',
                        statusMessage: rec.statusMessage
                    };
                    const element = createReceptorElement(receptorItem);
                    listContainer.appendChild(element);
                    // Hide delete button immediately if receptor is processing or referenced
                    const isProcessing = rec.status === 'in_progress' || rec.status === 'pending';
                    const isReferenced = referencedReceptors.has(rec.receptorID);
                    if (isProcessing || isReferenced || anyProcessing) {
                        const deleteBtn = element.querySelector('.btn-delete');
                        if (deleteBtn) {
                            deleteBtn.classList.remove('btn-visible');
                            deleteBtn.classList.add('btn-hidden');
                            deleteBtn.disabled = true;
                        }
                    }
                    // Update number label - use chronological order (1, 2, 3...)
                    const numberSpan = element.querySelector('.peptide-number');
                    if (numberSpan)
                        numberSpan.textContent = `${index + 1}`;
                    // Trust DynamoDB status for completed receptors
                    // Don't re-verify on page load to avoid false negatives from missing files
                    // User can manually check if needed
                }
                // Toggle visibility based on items
                const hasItems = listContainer.querySelector('.receptor-item');
                if (hasItems) {
                    listContainer.style.display = 'block';
                }
                else {
                    listContainer.style.display = 'none';
                }
                // Update delete buttons after loading receptors
                updateReceptorDeleteButtons();
            }
            catch (error) {
                console.error('Error loading session receptors:', error);
                const hasItems = listContainer.querySelector('.receptor-item');
                if (hasItems) {
                    const showToast = window.showToast;
                    if (showToast)
                        showToast('Could not load receptors. Wait a moment and try again.', 'warning', 5000);
                }
            }
            finally {
                // Always reset the loading flag
                isLoadingReceptors = false;
            }
        });
    }
    // Load existing session receptors into the list on init
    loadSessionReceptors();
    // --- Handle receptor submission ---
    submitBtn.addEventListener('click', () => __awaiter(this, void 0, void 0, function* () {
        // Prevent double-click by disabling button immediately
        if (submitBtn.disabled) {
            return;
        }
        // Disable submit button immediately to prevent double-clicks
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
        const rawInput = input.value;
        const pdbID = rawInput.trim().toUpperCase();
        // Validation: 4-letter PDB ID
        if (pdbID.length !== 4) {
            alert('Please enter a valid 4-letter PDB ID');
            // Re-enable button on validation failure
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit';
            return;
        }
        // Additional validation: check if all characters are alphanumeric
        if (!/^[A-Z0-9]{4}$/.test(pdbID)) {
            alert('Please enter a valid 4-character PDB ID (letters and numbers only)');
            // Re-enable button on validation failure
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit';
            return;
        }
        // Reset global state at the beginning of each new submission
        window.currentPdbID = null;
        window.currentReceptorID = null; // Reset receptorID for new submission
        window.currentBindingSiteID = null; // Reset binding site ID for new submission
        // step2Btn logic removed - no longer needed with new tab structure
        // Check if receptor with same PDB ID already exists in current list
        const existingReceptor = Array.from(listContainer.querySelectorAll('.receptor-item')).find(el => {
            var _a;
            const chip = el.querySelector('.chip-rec');
            const existingPdbID = ((_a = chip === null || chip === void 0 ? void 0 : chip.textContent) === null || _a === void 0 ? void 0 : _a.replace('Receptor: ', '').trim()) || '';
            return existingPdbID.toLowerCase() === pdbID.toLowerCase();
        });
        if (existingReceptor) {
            // Receptor already exists - just select it and clear input
            input.value = '';
            selectReceptorElement(existingReceptor);
            // Reset submit button when duplicate found
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit';
            return;
        }
        try {
            // Prepare the receptor and get the receptor ID FIRST, before creating UI element
            const result = yield prepareReceptor(pdbID);
            // Store the PDB ID for later use
            window.currentPdbID = pdbID;
            if (!result.submitted) {
                alert('Receptor preparation failed. Please try again.');
                // Reset submit button
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit';
                return; // Exit early if submission failed
            }
            // Check if we got a valid receptor ID (must be > 0)
            if (!result.receptorID || result.receptorID <= 0) {
                alert('Receptor preparation failed - no valid receptor ID received. Please try again.');
                // Reset submit button
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit';
                return; // Exit early if no valid ID
            }
            // Store the receptor ID
            window.currentReceptorID = result.receptorID;
            // NOW create the receptor item and UI element with the correct receptorID
            const receptorItem = {
                receptorID: result.receptorID,
                pdbID: pdbID,
                receptorReady: false,
                status: 'pending'
            };
            // Create and render UI element with correct receptorID
            const element = createReceptorElement(receptorItem);
            listContainer.appendChild(element);
            listContainer.style.display = 'block';
            // Debug: Verify stop button exists after adding to DOM
            setTimeout(() => {
                const stopBtnAfterAdd = element.querySelector('.btn-stop');
                if (stopBtnAfterAdd) {
                    // Test click handler
                    stopBtnAfterAdd.onclick = (ev) => {
                        ev.stopPropagation();
                        ev.preventDefault();
                    };
                }
            }, 100);
            // Update number label
            const numberSpan = element.querySelector('.peptide-number');
            if (numberSpan)
                numberSpan.textContent = `${listContainer.querySelectorAll('.receptor-item').length}`;
            // Update delete buttons
            updateReceptorDeleteButtons();
            // Mark element as just launched to prevent "still in progress" toast on first refresh
            element.setAttribute('data-just-launched', 'true');
            // Show toast for first launch (success - green)
            const showToast = window.showToast;
            if (showToast) {
                showToast(`Receptor preparation launched for receptor ${pdbID}.`, 'success', 4000);
            }
            // Refresh receptor status to ensure containerId is stored and UI is up to date
            // This ensures the stop button is only shown when containerId is actually available
            yield refreshReceptor(result.receptorID);
            // Remove the flag after first refresh
            element.removeAttribute('data-just-launched');
            // Update peptide details when receptor changes
            updatePeptideDetailsGlobal();
            // Select the receptor if it's ready (no need to reload entire list)
            const existing = listContainer.querySelector(`.receptor-item[data-receptor-id="${result.receptorID}"]`);
            if (existing) {
                const st = existing.querySelector('.peptide-status');
                const ready = Boolean(st && st.classList.contains('ready'));
                if (ready)
                    selectReceptorElement(existing);
            }
            // Reset submit button after backend call completes
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit';
            input.value = '';
            input.classList.remove('hidden');
        }
        catch (err) {
            console.error('❌ Error submitting receptor job:', err);
            console.error('❌ Error details:', err.message, err.stack);
            alert('Error submitting receptor job. Please try again.');
            // Reset submit button after error
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit';
            input.value = '';
            input.classList.remove('hidden');
        }
    }));
    function ensureReceptorListContainer() {
        let container = document.getElementById('receptor-list-container');
        if (!container) {
            const parent = document.getElementById('receptor-list-container') || document.getElementById('receptor-list-container-placeholder');
            container = document.createElement('div');
            container.id = 'receptor-list-container';
            container.className = 'receptor-list-container';
            container.innerHTML = '';
            container.style.display = 'none';
            // Insert right after the receptor input group
            const insertionPoint = document.getElementById('receptor-list-container') || document.getElementById('receptor-list-container');
            const wizardStep = document.getElementById('wizard-step-1');
            if (wizardStep)
                wizardStep.appendChild(container);
        }
        return container;
    }
    // Function to open binding site interface
    // Open binding site interface with optional pre-selection (used when clicking on screening items)
    function openBindingSiteInterfaceWithSelection(receptorID, pdbID, bindingSiteID, customBindingSiteId) {
        // Set the binding site state before opening the interface
        if (bindingSiteID !== undefined || customBindingSiteId !== undefined) {
            setBindingSiteState(receptorID, {
                bindingSiteID: bindingSiteID !== undefined ? bindingSiteID : null,
                customBindingSiteID: customBindingSiteId || null,
                submitted: true
            });
        }
        openBindingSiteInterface(receptorID, pdbID);
    }
    function openBindingSiteInterface(receptorID, pdbID) {
        const isDifferentReceptor = window.currentReceptorID !== receptorID;
        console.log(`[DEBUG] Opening binding site interface for receptor ${receptorID}, isDifferentReceptor: ${isDifferentReceptor}`);
        window.currentReceptorID = receptorID;
        window.currentPdbID = pdbID;
        // When switching receptor, clear global selection so we don't restore another receptor's custom binding site (wrong centroid)
        if (isDifferentReceptor) {
            window.selectedCustomBindingSite = null;
        }
        // Load binding site state for this receptor (per-receptor, so switching receptor shows that receptor's selection)
        const state = getBindingSiteState(receptorID);
        window.currentBindingSiteID = state.bindingSiteID;
        window.currentCustomBindingSiteID = state.customBindingSiteID;
        window.bindingSiteSubmitted = state.submitted;
        // Select the receptor element in the list first
        const receptorElement = document.querySelector(`[data-receptor-id="${receptorID}"]`);
        if (receptorElement) {
            selectReceptorElement(receptorElement);
            console.log(`[DEBUG] Selected receptor element for ID: ${receptorID}`);
        }
        // Hide the form title
        const formTitle = document.querySelector('.form-title');
        if (formTitle) {
            formTitle.style.display = 'none';
        }
        // Check if interface exists and reuse it
        let bindingSiteInterface = document.getElementById('binding-site-interface');
        if (isDifferentReceptor || !bindingSiteInterface) {
            console.log(`[DEBUG] Creating new interface - isDifferentReceptor: ${isDifferentReceptor}, existingInterface: ${!!bindingSiteInterface}`);
            // Remove existing interface only if switching receptors
            if (bindingSiteInterface && isDifferentReceptor) {
                console.log(`[DEBUG] Removing existing interface for different receptor`);
                bindingSiteInterface.remove();
                bindingSiteInterface = null; // Reset the reference
            }
            // Create a fresh binding site interface
            console.log(`[DEBUG] Creating fresh binding site interface`);
            bindingSiteInterface = document.createElement('div');
            bindingSiteInterface.id = 'binding-site-interface';
            bindingSiteInterface.className = 'binding-site-interface';
            bindingSiteInterface.innerHTML = `
        <div class="form-title">
          <div>
            <h3>🔗 Select Binding Site</h3>
            <p>Select binding site for receptor <b> ${pdbID} </b> </p>
          </div>
          <span id="back-to-receptors" class="btn-back-small" title="Back to receptors">↩</span>
        </div>
        
        <div class="form-group">
          <label class="form-label">Selection Mode:</label>
          <select id="binding-site-mode-select" class="form-control" style="width: 100%; margin-bottom: 8px;">
            <option value="binding-site">Binding Site (predefined)</option>
            <option value="custom">Custom Binding Sites (saved)</option>
            <option value="create-custom">Multiple Residues (create custom)</option>
          </select>
        </div>
        
        <div class="form-group" id="binding-site-mode-container">
          <div class="toggle-container">
            <label class="switch">
              <input type="checkbox" id="toggle-sel-bindingsite" />
              <span class="slider"></span>
            </label>
            <span id="toggle-sel-bindingsite-label" class="toggle-label">Binding site click selection disabled</span>
          </div>
        </div>
        
        <div class="form-group" id="custom-binding-sites-container" style="display: none;">
          <div id="custom-binding-sites-list-container" class="receptor-list-container" style="max-height: 240px; overflow-y: auto;">
            <div class="no-items" style="padding: 20px; text-align: center; color: #999;">
              No custom binding sites yet. Create one using "Multiple Residues" mode.
            </div>
          </div>
        </div>
        
        <div class="form-group" id="residue-mode-container" style="display: none;">
          <div class="toggle-container">
            <label class="switch">
              <input type="checkbox" id="toggle-sel-residues" />
              <span class="slider"></span>
            </label>
            <span id="toggle-sel-residues-label" class="toggle-label">Residue selection disabled</span>
          </div>
          
          <div id="selected-residues-display" class="selected-residues-box" style="margin-top: 12px;">
            <p class="text-instructions" id="binding-site-details">No residues selected</p>
            <button id="calculate-centroid" class="btn-secondary disabled" style="margin-top: 8px; width: 100%;">Calculate Centroid</button>
            <button id="clear-residue-selection">Clear Selection</button>
          </div>
        </div>
        
        <div class="form-group" id="binding-site-input-container">
          <div class="input-group">
            <input type="number" id="binding-site-input" placeholder="Enter binding site number" class="form-control" style="width: 100%;" />
            <p class="text-instructions" id="binding-site-details-bs">No binding site selected</p>
          </div>
        </div>
        
        <div class="form-buttons">
          <button id="submit-binding-site" class="btn-main disabled">🔗 Submit Binding Site</button>
        </div>
      `;
            const receptorTab = document.getElementById('receptor-tab');
            if (receptorTab) {
                console.log(`[DEBUG] Appending new interface to receptor tab`);
                receptorTab.appendChild(bindingSiteInterface);
            }
            else {
                console.error(`[DEBUG] Receptor tab not found!`);
            }
        }
        else {
            console.log(`[DEBUG] Reusing existing interface for same receptor`);
            // Update existing interface for same receptor
            const pElement = bindingSiteInterface.querySelector('p');
            if (pElement) {
                pElement.innerHTML = `Select binding site for receptor <b> ${pdbID} </b>`;
            }
            // Restore dropdown mode from state so custom binding site + receptor are shown (not just the green sphere)
            const modeSelectReuse = bindingSiteInterface.querySelector('#binding-site-mode-select');
            if (modeSelectReuse) {
                if (state.customBindingSiteID) {
                    modeSelectReuse.value = 'custom';
                }
                else if (state.bindingSiteID != null) {
                    modeSelectReuse.value = 'binding-site';
                }
                else {
                    modeSelectReuse.value = 'binding-site';
                }
            }
        }
        // Restore mode from state when creating new interface too (so returning to receptor shows custom site + receptor)
        const modeSelectInit = bindingSiteInterface === null || bindingSiteInterface === void 0 ? void 0 : bindingSiteInterface.querySelector('#binding-site-mode-select');
        if (modeSelectInit) {
            if (state.customBindingSiteID) {
                modeSelectInit.value = 'custom';
            }
            else if (state.bindingSiteID != null) {
                modeSelectInit.value = 'binding-site';
            }
        }
        // Hide the receptor list and show the binding site interface
        const receptorList = document.getElementById('wizard-step-1');
        if (receptorList) {
            receptorList.style.display = 'none';
        }
        // Show the interface
        if (bindingSiteInterface) {
            bindingSiteInterface.style.display = 'block';
            bindingSiteInterface.setAttribute('data-was-visible', 'true');
        }
        // Initialize binding site form
        initBindingSiteInterface(receptorID);
        // Re-initialize Mol* binding site handlers for the new interface
        if (window.molstarWrapper) {
            console.log('🔗 Calling bindBindingSiteEvents...');
            window.molstarWrapper.bindBindingSiteEvents();
        }
        else {
            console.log('⚠️ molstarWrapper not found');
        }
    }
    // Function to open screening interface (Screenings view for this receptor)
    function openScreeningInterface(receptorID, pdbID) {
        const isDifferentReceptor = window.currentReceptorID !== receptorID;
        window.currentReceptorID = receptorID;
        window.currentPdbID = pdbID;
        const receptorElement = document.querySelector(`[data-receptor-id="${receptorID}"]`);
        if (receptorElement) {
            selectReceptorElement(receptorElement);
        }
        const formTitle = document.querySelector('.form-title');
        if (formTitle) {
            formTitle.style.display = 'none';
        }
        let screeningInterface = document.getElementById('screening-interface');
        if (isDifferentReceptor || !screeningInterface) {
            if (screeningInterface && isDifferentReceptor) {
                screeningInterface.remove();
                screeningInterface = null;
            }
            screeningInterface = document.createElement('div');
            screeningInterface.id = 'screening-interface';
            screeningInterface.className = 'screening-interface';
            screeningInterface.setAttribute('data-receptor-id', String(receptorID));
            screeningInterface.setAttribute('data-pdb-id', pdbID);
            screeningInterface.innerHTML = `
        <div class="form-title screening-form-title">
          <div>
            <h3>🔍 Screenings</h3>
            <p>Screening results for receptor <b>${pdbID}</b></p>
          </div>
          <span id="back-to-receptors-screening" class="btn-back-small" title="Back to receptors">↩</span>
        </div>

        <div id="screening-list-container" class="screening-list-container">
          <!-- Placeholder: replaced by real items or "No screenings yet" -->
          <div class="no-items screening-no-items">No screenings yet</div>
        </div>

        <div class="screening-launch-section">
          <p id="screening-launch-text" class="text-instructions screening-launch-text peptide-details-style screening-launch-oneline">
            <span style="color: #3b82f6; font-weight: 600;">ℹ️</span> Select and submit a binding site to launch.
          </p>
          <div class="screening-launch-btn-wrap">
            <button id="launch-screening-btn" class="btn-main disabled" disabled>Launch Screening</button>
          </div>
        </div>
      `;
            const receptorTab = document.getElementById('receptor-tab');
            if (receptorTab) {
                receptorTab.appendChild(screeningInterface);
            }
            const backBtn = document.getElementById('back-to-receptors-screening');
            if (backBtn) {
                backBtn.addEventListener('click', () => {
                    const wizardStep1 = document.getElementById('wizard-step-1');
                    const screeningInterfaceEl = document.getElementById('screening-interface');
                    const formTitle = document.querySelector('.form-title');
                    if (wizardStep1)
                        wizardStep1.style.display = 'block';
                    if (screeningInterfaceEl)
                        screeningInterfaceEl.style.display = 'none';
                    if (formTitle)
                        formTitle.style.display = 'block';
                    updateReceptorDeleteButtons(); // Hide delete for receptors that now have screenings (e.g. just launched)
                    const receptorElement = document.querySelector(`[data-receptor-id="${receptorID}"]`);
                    if (receptorElement)
                        selectReceptorElement(receptorElement);
                });
            }
        }
        else {
            const pEl = screeningInterface.querySelector('.form-title p');
            if (pEl) {
                pEl.innerHTML = `Screening results for receptor <b>${pdbID}</b>`;
            }
        }
        const wizardStep1 = document.getElementById('wizard-step-1');
        if (wizardStep1) {
            wizardStep1.style.display = 'none';
        }
        if (screeningInterface) {
            screeningInterface.style.display = 'block';
        }
        initScreeningInterface(receptorID, pdbID);
    }
    // Update screening launch text from current receptor and binding site (no peptide needed for screening)
    function updateScreeningLaunchText() {
        const textEl = document.getElementById('screening-launch-text');
        const btnEl = document.getElementById('launch-screening-btn');
        if (!textEl)
            return;
        const pdbID = window.currentPdbID || '—';
        const bindingSiteLabel = window.currentBindingSiteID != null
            ? String(window.currentBindingSiteID)
            : window.currentCustomBindingSiteID
                ? window.currentCustomBindingSiteID
                : null;
        const hasBindingSite = window.bindingSiteSubmitted && (window.currentBindingSiteID != null || window.currentCustomBindingSiteID);
        if (hasBindingSite) {
            const p = String(pdbID).trim();
            const b = bindingSiteLabel != null ? String(bindingSiteLabel).trim() : '';
            const bindingSiteDisplay = bindingSiteLabel != null ? `binding site <b>${b}</b>` : '';
            textEl.innerHTML = `<span style="color: #3b82f6; font-weight: 600;">ℹ️</span> Screening will be launched with receptor <b>${p}</b> and ${bindingSiteDisplay}`;
            textEl.classList.remove('screening-launch-oneline');
            if (btnEl) {
                btnEl.classList.remove('disabled');
                btnEl.disabled = false;
            }
        }
        else {
            textEl.innerHTML = `<span style="color: #3b82f6; font-weight: 600;">ℹ️</span> Select and submit a binding site to launch.`;
            textEl.classList.add('screening-launch-oneline');
            if (btnEl) {
                btnEl.classList.add('disabled');
                btnEl.disabled = true;
            }
        }
    }
    function escapeHtml(s) {
        const div = document.createElement('div');
        div.textContent = s;
        return div.innerHTML;
    }
    window.updateScreeningLaunchText = updateScreeningLaunchText;
    function getStatusLabel(status, statusMessage) {
        if (status === 'completed')
            return { label: '✅ Ready', cls: 'ready' };
        if (status === 'failed' || status === 'job_submission_failed')
            return { label: '❌ Error', cls: 'error' };
        if (status === 'cancelled')
            return { label: '🚫 Cancelled', cls: 'error' };
        if (status === 'in_progress')
            return { label: '⏳ Processing...', cls: 'processing' };
        if (status === 'pending')
            return { label: '⏳ Pending...', cls: 'pending' };
        return { label: '⏳ Processing...', cls: 'processing' };
    }
    function createScreeningElement(item, receptorID, pdbID) {
        const { label: statusLabel, cls: statusClass } = getStatusLabel(item.status, item.statusMessage);
        const bindingSiteLabel = item.bindingSiteID === -1 && item.customBindingSiteId ? item.customBindingSiteId : String(item.bindingSiteID);
        const showCheck = item.status === 'pending' || item.status === 'in_progress';
        const showStop = item.status === 'pending' || item.status === 'in_progress';
        const showRestart = item.status === 'cancelled';
        const showDownload = item.status === 'completed';
        const canDelete = item.status !== 'pending' && item.status !== 'in_progress';
        const el = document.createElement('div');
        el.className = 'screening-item clickable';
        el.dataset.screeningId = item.screeningID;
        el.dataset.pdbId = pdbID;
        el.innerHTML = `
      <div class="screening-item-header">
        <div class="screening-left">
          <span class="peptide-status ${statusClass}">${statusLabel}</span>
        </div>
        <div class="peptide-controls">
          <button class="btn-check btn-icon ${!showCheck ? 'btn-hidden' : ''}" title="Refresh status">
            <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
              <path d="M3 21v-5h5"/>
            </svg>
          </button>
          <button class="btn-restart btn-icon ${!showRestart ? 'btn-hidden' : ''}" title="Restart screening">
            <svg class="restart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 4v6h6"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
          </button>
          <button class="btn-stop btn-icon ${!showStop ? 'btn-hidden' : ''}" title="Stop screening">
            <svg class="stop-icon" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" fill="currentColor"/>
              <rect x="8" y="8" width="8" height="8" fill="white" rx="1"/>
            </svg>
          </button>
          <button class="btn-download btn-icon ${!showDownload ? 'btn-hidden' : ''}" title="Download results">
            <svg class="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
          <button class="btn-delete btn-icon ${!canDelete ? 'btn-hidden' : ''}" title="Delete screening">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
      <div class="screening-meta">
        <span class="chip chip-site">Binding site: ${bindingSiteLabel}</span>
      </div>
      <div class="docking-progress-container btn-hidden" id="screening-progress-container-${item.screeningID}">
        <div class="docking-progress-bar">
          <div class="docking-progress-fill" id="screening-progress-fill-${item.screeningID}"></div>
          <div class="docking-progress-text" id="screening-progress-text-${item.screeningID}"></div>
        </div>
      </div>
      <div class="docking-status-message" id="screening-status-message-${item.screeningID}"></div>
    `;
        const checkBtn = el.querySelector('.btn-check');
        const restartBtn = el.querySelector('.btn-restart');
        const stopBtn = el.querySelector('.btn-stop');
        const downloadBtn = el.querySelector('.btn-download');
        const deleteBtn = el.querySelector('.btn-delete');
        if (checkBtn)
            checkBtn.addEventListener('click', (e) => { e.stopPropagation(); refreshScreeningStatus(item.screeningID, el, item); });
        if (restartBtn)
            restartBtn.addEventListener('click', (e) => { e.stopPropagation(); restartScreeningThenRefresh(item, receptorID); });
        if (stopBtn)
            stopBtn.addEventListener('click', (e) => { e.stopPropagation(); stopScreeningThenRefresh(item.screeningID, el); });
        if (downloadBtn)
            downloadBtn.addEventListener('click', (e) => { e.stopPropagation(); downloadScreeningResults(item.screeningID); });
        if (deleteBtn)
            deleteBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                confirmDeleteScreening(item.screeningID, pdbID, bindingSiteLabel, () => renderScreeningList(receptorID, pdbID));
            });
        // Click on item to show screening result in Mol*
        el.addEventListener('click', () => {
            console.log('[DEBUG] Screening item clicked:', item.screeningID);
            selectScreening(item, receptorID, pdbID);
        });
        // Show status message and progress bar ONLY for in_progress status
        const messageToShow = item.status === 'in_progress' ? (item.statusMessage || '') : '';
        updateScreeningProgressBarAndMessage(el, item.screeningID, messageToShow);
        return el;
    }
    function confirmDeleteScreening(screeningID, pdbID, bindingSiteLabel, onRefresh) {
        return __awaiter(this, void 0, void 0, function* () {
            const showConfirm = window.showConfirmationModal;
            if (!showConfirm) {
                console.error('showConfirmationModal not available');
                return;
            }
            const confirmed = yield showConfirm('Delete Screening', `Are you sure you want to delete the screening for receptor "${pdbID}" and binding site "${bindingSiteLabel}"? This action cannot be undone.`, 'Delete', 'Cancel');
            if (confirmed) {
                deleteScreeningThenRefresh(screeningID, onRefresh);
            }
        });
    }
    function restartScreeningThenRefresh(item, receptorID) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const bindingSiteID = item.bindingSiteID;
                const customBindingSite = item.customBindingSiteId;
                const result = yield launchScreeningAPI(receptorID, bindingSiteID, customBindingSite);
                if (result.screeningSubmitted) {
                    const showToast = window.showToast;
                    if (showToast)
                        showToast('Screening restarted', 'success', 3000);
                    const pdbID = window.currentPdbID || '';
                    yield renderScreeningList(receptorID, pdbID);
                }
            }
            catch (error) {
                console.error('Failed to restart screening:', error);
                const showToast = window.showToast;
                if (showToast)
                    showToast('Failed to restart screening', 'error', 3000);
            }
        });
    }
    function updateScreeningProgressBarAndMessage(el, screeningID, statusMessage) {
        var _a;
        // Search within the screening item element (el), not document - el may not be in DOM yet when called from createScreeningElement
        const progressContainer = el.querySelector(`#screening-progress-container-${screeningID}`);
        const progressFill = el.querySelector(`#screening-progress-fill-${screeningID}`);
        const progressText = el.querySelector(`#screening-progress-text-${screeningID}`);
        const statusMessageEl = el.querySelector(`#screening-status-message-${screeningID}`);
        if (statusMessageEl) {
            const msg = statusMessage || '';
            statusMessageEl.textContent = msg;
            statusMessageEl.style.display = msg ? 'block' : 'none';
        }
        if (!progressContainer || !progressFill || !progressText)
            return;
        if (statusMessage) {
            // Show progress bar only when we have "Done" followed by something (e.g. "Done 1 out of 4"), not for "running first out of ..."
            const hasDonePrefix = /done\s+.+out\s+of\s+\d+/i.test(statusMessage);
            if (!hasDonePrefix) {
                progressContainer.classList.add('btn-hidden');
                progressContainer.style.display = 'none';
                return;
            }
            // Match "done x out of y" or "done first/second/third/fourth out of N"
            let current = 0;
            let total = 0;
            const numericMatch = statusMessage.match(/done\s+(\d+)\s+out\s+of\s+(\d+)/i);
            const ordinalMatch = statusMessage.match(/done\s+(first|second|third|fourth|1st|2nd|3rd|4th)\s+out\s+of\s+(\d+)/i);
            if (numericMatch) {
                current = parseInt(numericMatch[1], 10);
                total = parseInt(numericMatch[2], 10);
            }
            else if (ordinalMatch) {
                const ord = { first: 1, second: 2, third: 3, fourth: 4, '1st': 1, '2nd': 2, '3rd': 3, '4th': 4 };
                current = (_a = ord[ordinalMatch[1].toLowerCase()]) !== null && _a !== void 0 ? _a : 0;
                total = parseInt(ordinalMatch[2], 10);
            }
            if (total > 0 && current >= 0) {
                const percentage = Math.round((current / total) * 100);
                progressFill.style.width = `${percentage}%`;
                progressText.textContent = `${percentage}%`;
                progressText.style.color = 'white';
                progressText.style.textShadow = '1px 0px 0px #1c9b6d, -1px 0px 0px #1c9b6d, 0px 1px 0px #1c9b6d, 0px -1px 0px #1c9b6d';
                progressText.style.fontWeight = 'bold';
                progressContainer.classList.remove('btn-hidden');
                progressContainer.style.display = 'block';
            }
            else {
                progressContainer.classList.add('btn-hidden');
                progressContainer.style.display = 'none';
            }
        }
        else {
            progressContainer.classList.add('btn-hidden');
            progressContainer.style.display = 'none';
        }
    }
    function refreshScreeningStatus(screeningID, el, item) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const previousStatus = item.status;
                const result = yield checkScreening(screeningID);
                console.log(`[DEBUG refreshScreeningStatus] screeningID=${screeningID}, status=${result.status}, statusMessage="${result.statusMessage}"`);
                const statusEl = el.querySelector('.peptide-status');
                if (statusEl) {
                    const { label, cls } = getStatusLabel(result.status, result.statusMessage);
                    statusEl.textContent = label;
                    statusEl.className = `peptide-status ${cls}`;
                }
                // Update status message and progress bar ONLY for in_progress status
                const messageToShow = result.status === 'in_progress' ? (result.statusMessage || '') : '';
                updateScreeningProgressBarAndMessage(el, screeningID, messageToShow);
                const checkBtn = el.querySelector('.btn-check');
                const restartBtn = el.querySelector('.btn-restart');
                const stopBtn = el.querySelector('.btn-stop');
                const downloadBtn = el.querySelector('.btn-download');
                const deleteBtn = el.querySelector('.btn-delete');
                const showCheck = result.status === 'pending' || result.status === 'in_progress';
                const showStop = result.status === 'pending' || result.status === 'in_progress';
                const showRestart = result.status === 'cancelled';
                const showDownload = result.status === 'completed';
                const canDelete = result.status !== 'pending' && result.status !== 'in_progress';
                if (checkBtn)
                    checkBtn.classList.toggle('btn-hidden', !showCheck);
                if (restartBtn)
                    restartBtn.classList.toggle('btn-hidden', !showRestart);
                if (stopBtn)
                    stopBtn.classList.toggle('btn-hidden', !showStop);
                if (downloadBtn)
                    downloadBtn.classList.toggle('btn-hidden', !showDownload);
                if (deleteBtn)
                    deleteBtn.classList.toggle('btn-hidden', !canDelete);
                // Toasts (same as receptor/peptide/docking): skip during bulk refresh or first refresh after launch
                const showToast = window.showToast;
                const isBulkRefresh = el.hasAttribute('data-refreshing-all');
                const isFirstRefreshAfterLaunch = el.hasAttribute('data-just-launched');
                if (isFirstRefreshAfterLaunch)
                    el.removeAttribute('data-just-launched');
                if (showToast && !isBulkRefresh) {
                    if (result.status === 'completed' && previousStatus !== 'completed') {
                        showToast('Screening is now ready.', 'success', 4000);
                    }
                    else if ((result.status === 'failed' || result.status === 'job_submission_failed') && previousStatus !== 'failed' && previousStatus !== 'job_submission_failed') {
                        showToast('Screening has failed.', 'error', 4000);
                    }
                    else if ((result.status === 'in_progress' || result.status === 'pending') && !isFirstRefreshAfterLaunch) {
                        showToast('Screening is still in progress.', 'info', 4000);
                    }
                }
            }
            catch (_) { }
        });
    }
    function stopScreeningThenRefresh(screeningID, el) {
        return __awaiter(this, void 0, void 0, function* () {
            const showToast = window.showToast;
            // Toast immediately when user confirms stop (same as receptor/peptide/docking)
            if (showToast)
                showToast('Screening will be stopped.', 'info', 4000);
            el.setAttribute('data-stopping', 'true');
            // Hide progress bar and status message immediately so old progression doesn't stay visible
            updateScreeningProgressBarAndMessage(el, screeningID, '');
            try {
                const result = yield stopScreening(screeningID);
                el.removeAttribute('data-stopping');
                const statusEl = el.querySelector('.peptide-status');
                if (statusEl) {
                    statusEl.textContent = '🚫 Cancelled';
                    statusEl.className = 'peptide-status error';
                }
                // Update button visibility: hide check/stop, show restart/delete
                const checkBtn = el.querySelector('.btn-check');
                const restartBtn = el.querySelector('.btn-restart');
                const stopBtn = el.querySelector('.btn-stop');
                const downloadBtn = el.querySelector('.btn-download');
                const deleteBtn = el.querySelector('.btn-delete');
                if (checkBtn)
                    checkBtn.classList.add('btn-hidden');
                if (restartBtn)
                    restartBtn.classList.remove('btn-hidden');
                if (stopBtn)
                    stopBtn.classList.add('btn-hidden');
                if (downloadBtn)
                    downloadBtn.classList.add('btn-hidden');
                if (deleteBtn)
                    deleteBtn.classList.remove('btn-hidden');
                if (showToast)
                    showToast('Screening has been cancelled successfully.', 'success', 4000);
            }
            catch (err) {
                el.removeAttribute('data-stopping');
                const msg = (err === null || err === void 0 ? void 0 : err.message) || '';
                if (msg.toLowerCase().includes('already completed') || msg.toLowerCase().includes('completed successfully')) {
                    if (showToast)
                        showToast('This screening has already completed successfully.', 'success', 4000);
                }
                else if (msg.toLowerCase().includes('already failed')) {
                    if (showToast)
                        showToast('This screening has already failed.', 'error', 4000);
                }
                else if (msg.toLowerCase().includes('cancelled') || msg.toLowerCase().includes('already been cancelled')) {
                    if (showToast)
                        showToast('This screening has already been cancelled.', 'warning', 4000);
                }
                else {
                    if (showToast)
                        showToast('Failed to stop screening. Please try again.', 'error', 4000);
                }
            }
        });
    }
    function deleteScreeningThenRefresh(screeningID, onRefresh) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const ok = yield deleteSessionScreening(screeningID);
                if (ok) {
                    onRefresh();
                    updateReceptorDeleteButtons(); // Re-show receptor delete button if no screenings left
                }
            }
            catch (_) { }
        });
    }
    function downloadScreeningResults(screeningID) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { getAuthHeaders } = yield import('../backendClient');
                const BACKEND_URL = window.BACKEND_URL || 'http://localhost:3001';
                const url = `${BACKEND_URL}/frontend/download-screening/${screeningID}`;
                const response = yield fetch(url, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    throw new Error('Download failed');
                }
                // Get filename from Content-Disposition header or use default
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = `screening-${screeningID}.zip`;
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }
                // Download file
                const blob = yield response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(downloadUrl);
                const showToast = window.showToast;
                if (showToast)
                    showToast('Screening results downloaded', 'success', 3000);
            }
            catch (error) {
                console.error('Failed to download screening:', error);
                const showToast = window.showToast;
                if (showToast)
                    showToast('Failed to download screening results', 'error', 3000);
            }
        });
    }
    // Select screening and display result in Mol*
    function selectScreening(item, receptorID, pdbID) {
        console.log('[DEBUG] selectScreening called for:', item.screeningID);
        // Remove selection from all other screenings
        const allScreenings = document.querySelectorAll('.screening-item');
        allScreenings.forEach(el => el.classList.remove('selected'));
        // Select this screening
        const screeningElement = document.querySelector(`[data-screening-id="${item.screeningID}"]`);
        if (screeningElement) {
            screeningElement.classList.add('selected');
            console.log('[DEBUG] Screening element selected:', item.screeningID);
            // Always load reprScreening.pdb (created at launch: receptor + selected binding site)
            console.log('[DEBUG] Loading screening representation (receptor + binding site)');
            viewScreeningResult(item, receptorID, pdbID);
        }
        else {
            console.error('[DEBUG] No element found for screening:', item.screeningID);
        }
    }
    // Load receptor associated with a screening (during processing)
    function loadReceptorForScreening(item, receptorID) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                console.log(`[Screening] Loading receptor for screening ${item.screeningID} (receptorID: ${receptorID})`);
                // Build receptor path
                const receptorPath = `/frontend/userSessions/dataBase/receptor-${String(receptorID).padStart(5, '0')}/rec.pdb`;
                // Show loading indicator
                const infoPanel = document.getElementById('structure-info');
                if (infoPanel) {
                    infoPanel.innerHTML = '<div class="loading-indicator">🔄 Loading receptor...</div>';
                }
                if (window.loadPdbInMolstar) {
                    console.log(`[Screening] Loading receptor from ${receptorPath}`);
                    window.loadPdbInMolstar(`screening-receptor-${receptorID}`, receptorPath);
                }
                else {
                    console.error('[Screening] loadPdbInMolstar not available');
                }
            }
            catch (error) {
                console.error('[Screening] Error loading receptor:', error);
            }
        });
    }
    // Load screening result (receptor with binding site)
    function viewScreeningResult(item, receptorID, pdbID) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                console.log(`[Screening] Loading result for screening ${item.screeningID}`);
                // Format binding site ID for path
                const bindingSiteForPath = (item.bindingSiteID === -1 && item.customBindingSiteId)
                    ? `b${item.customBindingSiteId}` // Custom: bA, bB, etc.
                    : `b${String(item.bindingSiteID).padStart(5, '0')}`; // Predefined: b00001, b00002, etc.
                // Build screening directory path
                const screeningDir = `screening-r${String(receptorID).padStart(5, '0')}-${bindingSiteForPath}`;
                // The job creates reprScreening.pdb in the screening directory
                const screeningPdbPath = `/frontend/userSessions/dataBase/${screeningDir}/reprScreening.pdb`;
                // Show loading indicator
                const infoPanel = document.getElementById('structure-info');
                if (infoPanel) {
                    infoPanel.innerHTML = '<div class="loading-indicator">🔄 Loading screening result...</div>';
                }
                if (window.loadPdbInMolstar) {
                    console.log(`[Screening] Loading screening result from ${screeningPdbPath}`);
                    window.loadPdbInMolstar(`screening-${item.screeningID}`, screeningPdbPath);
                    // If custom binding site, display green centroid sphere
                    if (item.bindingSiteID === -1 && item.customBindingSiteId && window.molstarWrapper) {
                        try {
                            const { getCustomBindingSites } = yield import('../backendClient');
                            const result = yield getCustomBindingSites(receptorID);
                            if (result && result.customBindingSites) {
                                const customBS = result.customBindingSites.find((bs) => bs.id === item.customBindingSiteId);
                                if (customBS && customBS.centroid) {
                                    console.log(`[Screening] Displaying centroid for custom binding site ${item.customBindingSiteId}:`, customBS.centroid);
                                    const wrapper = window.molstarWrapper;
                                    yield wrapper.displayCentroid(customBS.centroid);
                                }
                            }
                        }
                        catch (error) {
                            console.error('[Screening] Failed to display custom binding site centroid:', error);
                        }
                    }
                }
                else {
                    console.error('[Screening] loadPdbInMolstar not available');
                }
            }
            catch (error) {
                console.error('[Screening] Error loading screening result:', error);
            }
        });
    }
    const SCREENING_LOAD_TIMEOUT_MS = 15000; // 15s max wait so "Loading..." doesn't hang forever
    function renderScreeningList(receptorID, pdbID) {
        return __awaiter(this, void 0, void 0, function* () {
            const container = document.getElementById('screening-list-container');
            if (!container)
                return;
            container.innerHTML = '<div class="no-items screening-no-items" style="opacity:0.7">Loading…</div>';
            try {
                const data = yield Promise.race([
                    getSessionScreenings(receptorID),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), SCREENING_LOAD_TIMEOUT_MS))
                ]);
                const screeningInterface = container.closest('#screening-interface');
                const currentReceptorId = screeningInterface === null || screeningInterface === void 0 ? void 0 : screeningInterface.getAttribute('data-receptor-id');
                if (currentReceptorId !== null && String(receptorID) !== currentReceptorId) {
                    return;
                }
                if ((data === null || data === void 0 ? void 0 : data.error) === 'rate_exceeded') {
                    container.innerHTML = '<div class="no-items screening-no-items" style="opacity:0.9">Too many requests. Please wait a moment and <button type="button" class="btn-link" id="screening-retry-btn">try again</button>.</div>';
                    const retryBtn = document.getElementById('screening-retry-btn');
                    if (retryBtn)
                        retryBtn.addEventListener('click', () => renderScreeningList(receptorID, pdbID));
                    return;
                }
                const list = (data === null || data === void 0 ? void 0 : data.screenings) || [];
                if (list.length === 0) {
                    container.innerHTML = '<div class="no-items screening-no-items">No screenings yet</div>';
                    return;
                }
                container.innerHTML = '';
                list.forEach((item) => container.appendChild(createScreeningElement(item, receptorID, pdbID)));
            }
            catch (error) {
                const screeningInterface = container.closest('#screening-interface');
                const currentReceptorId = screeningInterface === null || screeningInterface === void 0 ? void 0 : screeningInterface.getAttribute('data-receptor-id');
                if (currentReceptorId !== null && String(receptorID) !== currentReceptorId)
                    return;
                const isTimeout = error instanceof Error && error.message === 'timeout';
                if (isTimeout) {
                    container.innerHTML = '<div class="no-items screening-no-items" style="opacity:0.9">Loading is taking longer than usual. <button type="button" class="btn-link" id="screening-retry-btn">Try again</button></div>';
                    const retryBtn = document.getElementById('screening-retry-btn');
                    if (retryBtn)
                        retryBtn.addEventListener('click', () => renderScreeningList(receptorID, pdbID));
                }
                else {
                    console.log('[DEBUG] Could not load screenings:', error);
                    container.innerHTML = '<div class="no-items screening-no-items" style="opacity:0.9">Could not load. <button type="button" class="btn-link" id="screening-retry-btn">Try again</button></div>';
                    const retryBtn = document.getElementById('screening-retry-btn');
                    if (retryBtn)
                        retryBtn.addEventListener('click', () => renderScreeningList(receptorID, pdbID));
                }
            }
        });
    }
    // Expose for statusManager global refresh (so screening lists get updated after "Refresh all status")
    window.refreshScreeningListForReceptor = (receptorID, pdbID) => __awaiter(this, void 0, void 0, function* () {
        yield renderScreeningList(receptorID, pdbID);
    });
    function initScreeningInterface(receptorID, pdbID) {
        updateScreeningLaunchText();
        renderScreeningList(receptorID, pdbID);
        const launchBtn = document.getElementById('launch-screening-btn');
        if (launchBtn && !launchBtn.dataset.bound) {
            launchBtn.dataset.bound = '1';
            launchBtn.addEventListener('click', () => __awaiter(this, void 0, void 0, function* () {
                console.log('[DEBUG] Launch Screening button clicked');
                if (launchBtn.disabled || launchBtn.classList.contains('disabled')) {
                    console.log('[DEBUG] Button is disabled, returning');
                    return;
                }
                // Use the receptor that owns THIS screening interface (not window.currentReceptorID which can be another tab)
                const screeningInterfaceEl = document.getElementById('screening-interface');
                const interfaceReceptorID = screeningInterfaceEl === null || screeningInterfaceEl === void 0 ? void 0 : screeningInterfaceEl.getAttribute('data-receptor-id');
                const interfacePdbID = (screeningInterfaceEl === null || screeningInterfaceEl === void 0 ? void 0 : screeningInterfaceEl.getAttribute('data-pdb-id')) || '';
                const receptorIDToUse = interfaceReceptorID != null ? parseInt(interfaceReceptorID, 10) : NaN;
                if (!Number.isFinite(receptorIDToUse)) {
                    console.warn('[DEBUG] Missing screening interface receptor ID');
                    const showToast = window.showToast;
                    if (showToast)
                        showToast('Cannot determine receptor. Please open Screenings again.', 'error', 4000);
                    return;
                }
                const bindingSiteID = window.currentBindingSiteID != null ? window.currentBindingSiteID : -1;
                const customBindingSite = window.currentCustomBindingSiteID || undefined;
                console.log('[DEBUG] Launching screening for receptor (from interface):', receptorIDToUse, 'pdb:', interfacePdbID, 'bindingSiteID:', bindingSiteID, 'custom:', customBindingSite);
                try {
                    const result = yield launchScreeningAPI(receptorIDToUse, bindingSiteID, customBindingSite);
                    console.log('[DEBUG] Launch result:', result);
                    if (result.screeningSubmitted) {
                        const showToast = window.showToast;
                        if (showToast)
                            showToast('Screening launched.', 'success', 4000);
                        // Refresh the list for THIS interface so the new screening appears in processing
                        yield renderScreeningList(receptorIDToUse, interfacePdbID);
                        const container = document.getElementById('screening-list-container');
                        const lastItem = container === null || container === void 0 ? void 0 : container.querySelector('.screening-item:last-child');
                        if (lastItem)
                            lastItem.setAttribute('data-just-launched', 'true');
                    }
                }
                catch (error) {
                    console.error('[DEBUG] Launch screening error:', error);
                    const showToast = window.showToast;
                    if (showToast)
                        showToast('Failed to launch screening. Please try again.', 'error', 4000);
                }
            }));
        }
    }
    // Function to initialize binding site interface
    function initBindingSiteInterface(receptorID) {
        // Back button functionality
        const backBtn = document.getElementById('back-to-receptors');
        if (backBtn) {
            backBtn.addEventListener('click', () => {
                // Disable all selection toggles
                const bindingSiteToggle = document.getElementById('toggle-sel-bindingsite');
                const residueToggle = document.getElementById('toggle-sel-residues');
                if (bindingSiteToggle) {
                    bindingSiteToggle.checked = false;
                }
                if (residueToggle) {
                    residueToggle.checked = false;
                }
                // Disable selection modes in Mol*
                if (window.molstarWrapper) {
                    window.molstarWrapper.bindingSiteSelectionMode = false;
                    window.molstarWrapper.residueSelectionMode = false;
                    window.molstarWrapper.plugin.selectionMode = false;
                    window.molstarWrapper.clearResidueSelection();
                }
                // Mark binding site interface as closed
                const bindingSiteInterface = document.getElementById('binding-site-interface');
                if (bindingSiteInterface) {
                    bindingSiteInterface.setAttribute('data-was-visible', 'false');
                }
                // Restore original receptor tab content
                restoreReceptorTab();
            });
        }
        // Mode selection handler
        const modeSelect = document.getElementById('binding-site-mode-select');
        const bindingSiteContainer = document.getElementById('binding-site-mode-container');
        const residueContainer = document.getElementById('residue-mode-container');
        const bindingSiteInputContainer = document.getElementById('binding-site-input-container');
        const selectedResiduesDisplay = document.getElementById('selected-residues-display');
        // Function to validate submit button based on current mode
        function validateSubmitButton() {
            const modeSelect = document.getElementById('binding-site-mode-select');
            const mode = (modeSelect === null || modeSelect === void 0 ? void 0 : modeSelect.value) || 'binding-site';
            const submitBtn = document.getElementById('submit-binding-site');
            if (!submitBtn)
                return;
            if (mode === 'binding-site') {
                // Predefined binding site mode
                const input = document.getElementById('binding-site-input');
                const val = (input === null || input === void 0 ? void 0 : input.value.trim()) || '';
                const maxBindingSites = window.nBindingSites || 0;
                if (val === '') {
                    submitBtn.classList.add('disabled');
                    return;
                }
                const numVal = parseInt(val);
                const isValid = /^\d+$/.test(val) && numVal >= 1 && numVal <= maxBindingSites;
                if (isValid) {
                    submitBtn.classList.remove('disabled');
                }
                else {
                    submitBtn.classList.add('disabled');
                }
            }
            else if (mode === 'custom') {
                // Custom binding site mode: check if one is selected
                const selectedCustom = window.selectedCustomBindingSite;
                if (selectedCustom) {
                    submitBtn.classList.remove('disabled');
                }
                else {
                    submitBtn.classList.add('disabled');
                }
            }
            else if (mode === 'create-custom') {
                // Create custom mode: check if at least 2 residues selected
                const customData = window.customBindingSiteData;
                if (customData && customData.residues && customData.residues.length >= 2) {
                    submitBtn.classList.remove('disabled');
                }
                else {
                    submitBtn.classList.add('disabled');
                }
            }
        }
        // Expose validateSubmitButton globally for use in molstarViewer
        window.validateBindingSiteSubmit = validateSubmitButton;
        // Function to update UI based on current mode
        function updateModeUI(mode) {
            return __awaiter(this, void 0, void 0, function* () {
                const bindingSiteToggle = document.getElementById('toggle-sel-bindingsite');
                const residueToggle = document.getElementById('toggle-sel-residues');
                const customBindingSitesContainer = document.getElementById('custom-binding-sites-container');
                // Hide all containers first
                bindingSiteContainer.style.display = 'none';
                residueContainer.style.display = 'none';
                bindingSiteInputContainer.style.display = 'none';
                selectedResiduesDisplay.style.display = 'none';
                if (customBindingSitesContainer)
                    customBindingSitesContainer.style.display = 'none';
                // Disable all toggles
                if (bindingSiteToggle)
                    bindingSiteToggle.checked = false;
                if (residueToggle)
                    residueToggle.checked = false;
                // Disable all selection modes in Molstar
                if (window.molstarWrapper) {
                    window.molstarWrapper.bindingSiteSelectionMode = false;
                    window.molstarWrapper.residueSelectionMode = false;
                    window.molstarWrapper.plugin.selectionMode = false;
                    // Remove any displayed centroid when changing modes
                    window.molstarWrapper.removeCentroidMarker();
                }
                // Show appropriate container based on mode
                if (mode === 'binding-site') {
                    // Predefined binding sites
                    bindingSiteContainer.style.display = 'block';
                    bindingSiteInputContainer.style.display = 'block';
                }
                else if (mode === 'custom') {
                    // Custom binding sites list (saved)
                    if (customBindingSitesContainer)
                        customBindingSitesContainer.style.display = 'block';
                    yield loadCustomBindingSitesList();
                }
                else if (mode === 'create-custom') {
                    // Create new custom binding site (multiple residues)
                    residueContainer.style.display = 'block';
                    selectedResiduesDisplay.style.display = 'block';
                    // Initialize UI with current residue selection if any
                    if (window.molstarWrapper) {
                        const wrapper = window.molstarWrapper;
                        wrapper.updateResidueSelectionUI();
                    }
                }
                // Validate submit button after mode change
                validateSubmitButton();
            });
        }
        // Function to load custom binding sites list from API
        function loadCustomBindingSitesList(selectBindingSiteId) {
            return __awaiter(this, void 0, void 0, function* () {
                const listContainer = document.getElementById('custom-binding-sites-list-container');
                if (!listContainer)
                    return;
                // Show loading state
                listContainer.innerHTML = `
        <div class="no-items" style="padding: 20px; text-align: center; color: #999;">
          Loading...
        </div>
      `;
                try {
                    const { getCustomBindingSites } = yield import('../backendClient');
                    const result = yield getCustomBindingSites(receptorID);
                    if (!result || !result.customBindingSites) {
                        listContainer.innerHTML = `
            <div class="no-items" style="padding: 20px; text-align: center; color: #999;">
              Failed to load custom binding sites
            </div>
          `;
                        return;
                    }
                    let customBindingSites = result.customBindingSites;
                    if (customBindingSites.length === 0) {
                        listContainer.innerHTML = `
            <div class="no-items" style="padding: 20px; text-align: center; color: #999;">
              No custom binding sites yet. Create one using "Multiple Residues" mode.
            </div>
          `;
                        // Clear any stale centroid (e.g. 3PPO: no sites but sphere from previous receptor still visible)
                        if (window.molstarWrapper) {
                            window.molstarWrapper.removeCentroidMarker();
                        }
                        window.selectedCustomBindingSite = null;
                        return;
                    }
                    // Sort by creation date (most recent first)
                    customBindingSites = [...customBindingSites].sort((a, b) => {
                        const dateA = new Date(a.createdAt).getTime();
                        const dateB = new Date(b.createdAt).getTime();
                        return dateB - dateA; // Descending order (newest first)
                    });
                    listContainer.innerHTML = '';
                    customBindingSites.forEach((bs) => {
                        const item = document.createElement('div');
                        item.className = 'receptor-item custom-binding-site-item';
                        item.setAttribute('data-binding-site-id', bs.id);
                        const centroidStr = `(${bs.centroid.x.toFixed(2)}, ${bs.centroid.y.toFixed(2)}, ${bs.centroid.z.toFixed(2)})`;
                        const date = new Date(bs.createdAt);
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const year = date.getFullYear().toString().slice(-2);
                        const dateStr = `${day}/${month}/${year}`;
                        item.innerHTML = `
            <div class="receptor-item-header">
              <div class="receptor-left">
                <span class="peptide-number">${bs.id}</span>
                <span class="chip chip-site">${dateStr}</span>
                <span class="chip" style="background-color: #e3f2fd; color: #1976d2; border: 1px solid #90caf9; border-left: 3px solid #1976d2;">Coords: ${centroidStr}</span>
              </div>
              <div class="peptide-controls">
                <span class="delete-custom-bs-btn" data-bs-id="${bs.id}" style="
                  color: #f44336;
                  font-size: 20px;
                  font-weight: bold;
                  cursor: pointer;
                  padding: 4px 8px;
                  line-height: 1;
                  user-select: none;
                  transition: opacity 0.2s;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                " title="Delete">×</span>
              </div>
            </div>
          `;
                        // Click handler to select this custom binding site
                        const deleteBtn = item.querySelector('.delete-custom-bs-btn');
                        // Add hover effects like receptors (using CSS classes instead of inline styles)
                        item.addEventListener('mouseenter', () => {
                            item.style.backgroundColor = '#f5f5f5';
                        });
                        item.addEventListener('mouseleave', () => {
                            item.style.backgroundColor = '';
                        });
                        item.addEventListener('click', (e) => {
                            if (e.target.classList.contains('delete-custom-bs-btn') ||
                                e.target.closest('.delete-custom-bs-btn')) {
                                return; // Let delete button handler handle this
                            }
                            // Remove selection from all items
                            listContainer.querySelectorAll('.custom-binding-site-item').forEach(el => {
                                el.classList.remove('selected');
                            });
                            // Add selection to clicked item
                            item.classList.add('selected');
                            selectCustomBindingSite(bs);
                        });
                        // Delete button handler
                        if (deleteBtn) {
                            deleteBtn.addEventListener('click', (e) => {
                                e.stopPropagation();
                                deleteCustomBindingSite(bs.id);
                            });
                            // Hover effect for delete button
                            deleteBtn.addEventListener('mouseenter', () => {
                                deleteBtn.style.opacity = '0.7';
                            });
                            deleteBtn.addEventListener('mouseleave', () => {
                                deleteBtn.style.opacity = '1';
                            });
                        }
                        // Hover effect for item
                        item.addEventListener('mouseenter', () => {
                            item.style.backgroundColor = '#f5f5f5';
                        });
                        item.addEventListener('mouseleave', () => {
                            item.style.backgroundColor = 'transparent';
                        });
                        listContainer.appendChild(item);
                    });
                    // Restore selection: use only state or first item (selectedCustomBindingSite is cleared when switching receptor)
                    // Priority: 1) selectBindingSiteId parameter, 2) state (currentCustomBindingSiteID), 3) first one
                    let bindingSiteToSelect = null;
                    if (selectBindingSiteId) {
                        bindingSiteToSelect = customBindingSites.find((bs) => bs.id === selectBindingSiteId);
                    }
                    else {
                        const fromState = window.currentCustomBindingSiteID;
                        if (fromState) {
                            bindingSiteToSelect = customBindingSites.find((bs) => bs.id === fromState);
                        }
                        if (!bindingSiteToSelect && customBindingSites.length > 0) {
                            bindingSiteToSelect = customBindingSites[0];
                        }
                    }
                    if (bindingSiteToSelect) {
                        const selectedItem = listContainer.querySelector(`[data-binding-site-id="${bindingSiteToSelect.id}"]`);
                        if (selectedItem) {
                            selectedItem.classList.add('selected');
                            selectCustomBindingSite(bindingSiteToSelect);
                        }
                    }
                }
                catch (error) {
                    console.error('Error loading custom binding sites:', error);
                    listContainer.innerHTML = `
          <div class="no-items" style="padding: 20px; text-align: center; color: #999;">
            Error loading custom binding sites
          </div>
        `;
                }
            });
        }
        // Function to select a custom binding site
        function selectCustomBindingSite(bs) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!bs)
                    return;
                console.log('Selected custom binding site:', bs.id, 'centroid:', bs === null || bs === void 0 ? void 0 : bs.centroid);
                // Store selected custom binding site globally (for this receptor only - cleared when switching receptor)
                window.selectedCustomBindingSite = bs;
                // Load receptor first so centroid is in the same coordinate system; wait for load to finish
                if (window.loadPdbInMolstar) {
                    const receptorPath = `/frontend/userSessions/dataBase/receptor-${String(receptorID).padStart(5, '0')}/rec.pdb`;
                    yield window.loadPdbInMolstar(`receptor-${receptorID}`, receptorPath);
                    // Brief delay so Mol* has the receptor fully rendered before placing centroid (avoids wrong/offset position)
                    yield new Promise((r) => setTimeout(r, 150));
                }
                // Use this binding site's centroid only (never from another receptor or another site)
                const centroid = (bs === null || bs === void 0 ? void 0 : bs.centroid)
                    ? {
                        x: Number(bs.centroid.x),
                        y: Number(bs.centroid.y),
                        z: Number(bs.centroid.z)
                    }
                    : null;
                if (window.molstarWrapper && centroid && !Number.isNaN(centroid.x + centroid.y + centroid.z)) {
                    const wrapper = window.molstarWrapper;
                    yield wrapper.displayCentroid(centroid);
                }
                validateSubmitButton();
            });
        }
        // Function to delete a custom binding site
        function deleteCustomBindingSite(bsId) {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (!confirm(`Delete Custom Binding Site ${bsId}?`)) {
                    return;
                }
                try {
                    const { deleteCustomBindingSite: deleteAPI } = yield import('../backendClient');
                    const result = yield deleteAPI(receptorID, bsId);
                    if (!result) {
                        alert(`Failed to delete custom binding site: Unknown error`);
                        return;
                    }
                    // Reload list
                    yield loadCustomBindingSitesList();
                    // Clear selection if this was the selected one
                    if (((_a = window.selectedCustomBindingSite) === null || _a === void 0 ? void 0 : _a.id) === bsId) {
                        window.selectedCustomBindingSite = null;
                        // Remove centroid marker
                        if (window.molstarWrapper) {
                            window.molstarWrapper.removeCentroidMarker();
                        }
                        validateSubmitButton();
                    }
                }
                catch (error) {
                    console.error('Error deleting custom binding site:', error);
                    alert('Failed to delete custom binding site');
                }
            });
        }
        if (modeSelect && bindingSiteContainer && residueContainer && bindingSiteInputContainer && selectedResiduesDisplay) {
            // Initialize UI based on current dropdown value
            const currentMode = modeSelect.value || 'binding-site';
            updateModeUI(currentMode); // Don't await during initialization
            // Listen for mode changes
            modeSelect.addEventListener('change', () => __awaiter(this, void 0, void 0, function* () {
                yield updateModeUI(modeSelect.value);
            }));
        }
        // Residue selection toggle handler
        const residueToggle = document.getElementById('toggle-sel-residues');
        const residueToggleLabel = document.getElementById('toggle-sel-residues-label');
        if (residueToggle && residueToggleLabel && window.molstarWrapper) {
            residueToggle.addEventListener('change', () => {
                const wrapper = window.molstarWrapper;
                wrapper.residueSelectionMode = residueToggle.checked;
                wrapper.bindingSiteSelectionMode = false;
                // Update binding site toggle
                const bindingSiteToggle = document.getElementById('toggle-sel-bindingsite');
                if (bindingSiteToggle) {
                    bindingSiteToggle.checked = false;
                }
                if (wrapper.residueSelectionMode) {
                    wrapper.plugin.selectionMode = true;
                    residueToggleLabel.textContent = 'Click residues to select them';
                }
                else {
                    wrapper.plugin.selectionMode = false;
                    residueToggleLabel.textContent = 'Residue selection disabled';
                }
                // Validate submit button
                validateSubmitButton();
            });
        }
        // Calculate centroid button
        const calculateCentroidBtn = document.getElementById('calculate-centroid');
        if (calculateCentroidBtn && window.molstarWrapper) {
            calculateCentroidBtn.addEventListener('click', () => {
                const wrapper = window.molstarWrapper;
                if (wrapper.selectedResidues && wrapper.selectedResidues.length >= 2) {
                    wrapper.calculateAndDisplayCentroid();
                }
                else {
                    alert('Please select at least 2 residues to calculate the centroid.');
                }
            });
        }
        // Clear residue selection button
        const clearResidueBtn = document.getElementById('clear-residue-selection');
        if (clearResidueBtn && window.molstarWrapper) {
            clearResidueBtn.addEventListener('click', () => {
                window.molstarWrapper.clearResidueSelection();
                // Validate submit button after clearing
                validateSubmitButton();
            });
        }
        // Initialize display based on default mode (binding-site)
        // Note: updateModeUI will handle the display logic, so we don't need to set defaults here
        // Initialize binding site form logic (reuse existing logic from bindingSiteForm.ts)
        const input = document.getElementById('binding-site-input');
        const detailsText = document.getElementById('binding-site-details-bs');
        const submitBtn = document.getElementById('submit-binding-site');
        if (!input || !detailsText || !submitBtn)
            return;
        // Get binding site information
        getBindingSiteInfo(receptorID).then(bindingSiteInfo => {
            var _a;
            if (bindingSiteInfo.nBindingSites > 0) {
                // Load receptor with binding sites in Mol*
                if (bindingSiteInfo.receptorWithPocketsPath) {
                    (_a = window.loadPdbInMolstar) === null || _a === void 0 ? void 0 : _a.call(window, 'receptor_with_pockets', bindingSiteInfo.receptorWithPocketsPath);
                }
                // Configure input based on available binding sites
                input.min = '1';
                input.max = String(bindingSiteInfo.nBindingSites);
                input.placeholder = `Enter binding site number (1-${bindingSiteInfo.nBindingSites})`;
                // Store binding site info globally for validation
                window.nBindingSites = bindingSiteInfo.nBindingSites;
                window.receptorWithPocketsPath = bindingSiteInfo.receptorWithPocketsPath;
                // Restore state if binding site was already submitted
                if (window.currentBindingSiteID && window.bindingSiteSubmitted) {
                    console.log(`[DEBUG] Restoring binding site state: ${window.currentBindingSiteID}`);
                    input.value = String(window.currentBindingSiteID);
                    detailsText.textContent = `Binding site ${window.currentBindingSiteID} submitted`;
                    submitBtn.classList.remove('disabled');
                }
                else {
                    console.log(`[DEBUG] Starting with empty binding site state`);
                    input.value = '';
                    detailsText.textContent = 'No binding site selected';
                    submitBtn.classList.add('disabled');
                }
            }
            else {
                input.disabled = true;
                input.placeholder = 'No binding sites available';
                detailsText.textContent = 'No binding sites available';
            }
        }).catch(error => {
            console.error('❌ Error getting binding site info:', error);
            detailsText.textContent = 'Error loading binding sites';
        }).finally(() => {
            // Validate submit button after initialization
            validateSubmitButton();
        });
        // Add input validation
        input.addEventListener('input', () => {
            var _a, _b;
            const val = input.value.trim();
            const maxBindingSites = window.nBindingSites || 0;
            if (val === '') {
                detailsText.textContent = 'No binding site selected';
                submitBtn.classList.add('disabled');
                return;
            }
            const numVal = parseInt(val);
            const isValid = /^\d+$/.test(val) && numVal >= 1 && numVal <= maxBindingSites;
            if (isValid) {
                detailsText.textContent = `Binding site ${val} selected`;
                // Update the binding site input field in Mol* if available
                if (window.molstarWrapper) {
                    (_b = (_a = window.molstarWrapper).updateBindingSiteInput) === null || _b === void 0 ? void 0 : _b.call(_a, numVal);
                }
            }
            else {
                detailsText.textContent = `Invalid binding site. Must be 1-${maxBindingSites}`;
            }
            // Validate submit button
            validateSubmitButton();
        });
        // Add submit button functionality
        submitBtn.addEventListener('click', () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f;
            const modeSelect = document.getElementById('binding-site-mode-select');
            const mode = (modeSelect === null || modeSelect === void 0 ? void 0 : modeSelect.value) || 'binding-site';
            if (mode === 'binding-site') {
                // Predefined binding site mode
                const val = input.value.trim();
                const maxBindingSites = window.nBindingSites || 0;
                if (val === '') {
                    detailsText.textContent = 'No binding site selected';
                    return;
                }
                const numVal = parseInt(val);
                const isValid = /^\d+$/.test(val) && numVal >= 1 && numVal <= maxBindingSites;
                if (isValid) {
                    window.currentBindingSiteID = numVal;
                    window.currentCustomBindingSiteID = null;
                    window.selectedCustomBindingSite = null;
                    window.bindingSiteSubmitted = true;
                    setBindingSiteState(receptorID, { bindingSiteID: numVal, customBindingSiteID: null, submitted: true });
                    (_b = (_a = window).updateScreeningLaunchText) === null || _b === void 0 ? void 0 : _b.call(_a);
                    detailsText.textContent = `Binding site ${val} submitted`;
                    submitBtn.classList.add('disabled');
                    // Update peptide details to show docking availability
                    if (typeof window.updatePeptideDetailsGlobal === 'function') {
                        window.updatePeptideDetailsGlobal();
                    }
                    console.log(`[DEBUG] Binding site ${val} submitted for receptor ${receptorID}`);
                    // Return to receptor tab
                    restoreReceptorTab();
                }
                else {
                    detailsText.textContent = `Invalid binding site. Must be 1-${maxBindingSites}`;
                }
            }
            else if (mode === 'custom') {
                // Custom binding site mode (already saved)
                const selectedCustom = window.selectedCustomBindingSite;
                if (!selectedCustom) {
                    return;
                }
                window.currentBindingSiteID = null;
                window.currentCustomBindingSiteID = selectedCustom.id;
                window.bindingSiteSubmitted = true;
                setBindingSiteState(receptorID, { bindingSiteID: null, customBindingSiteID: selectedCustom.id, submitted: true });
                (_d = (_c = window).updateScreeningLaunchText) === null || _d === void 0 ? void 0 : _d.call(_c);
                submitBtn.classList.add('disabled');
                // Mark the selected item visually
                const listContainer = document.getElementById('custom-binding-sites-list-container');
                if (listContainer) {
                    listContainer.querySelectorAll('.receptor-item').forEach(el => {
                        el.classList.remove('selected');
                    });
                    const selectedItem = listContainer.querySelector(`[data-binding-site-id="${selectedCustom.id}"]`);
                    if (selectedItem) {
                        selectedItem.classList.add('selected');
                    }
                }
                // Update peptide details to show docking availability
                if (typeof window.updatePeptideDetailsGlobal === 'function') {
                    window.updatePeptideDetailsGlobal();
                }
                console.log(`[DEBUG] Custom binding site ${selectedCustom.id} submitted for receptor ${receptorID}`);
                // Return to receptor tab
                restoreReceptorTab();
            }
            else if (mode === 'create-custom') {
                // Create new custom binding site mode
                const customData = window.customBindingSiteData;
                if (!customData || !customData.residues || customData.residues.length < 2) {
                    const detailsTextResidues = document.getElementById('binding-site-details');
                    if (detailsTextResidues) {
                        detailsTextResidues.textContent = 'Please select at least 2 residues';
                    }
                    return;
                }
                // Calculate centroid
                const wrapper = window.molstarWrapper;
                if (!wrapper) {
                    console.error('Molstar wrapper not found');
                    return;
                }
                const centroid = wrapper.calculateCentroid(wrapper.selectedResidues);
                if (!centroid) {
                    const detailsTextResidues = document.getElementById('binding-site-details');
                    if (detailsTextResidues) {
                        detailsTextResidues.textContent = 'Failed to calculate centroid';
                    }
                    return;
                }
                // Show loading state
                const detailsTextResidues = document.getElementById('binding-site-details');
                if (detailsTextResidues) {
                    detailsTextResidues.textContent = 'Creating custom binding site...';
                }
                submitBtn.classList.add('disabled');
                try {
                    const { createCustomBindingSite } = yield import('../backendClient');
                    const result = yield createCustomBindingSite(receptorID, centroid, customData.residues);
                    if (!result.created || !result.customBindingSite) {
                        if (detailsTextResidues) {
                            // Show specific error message from backend (e.g., duplicate centroid)
                            const errorMsg = result.error || result.message || 'Unknown error';
                            detailsTextResidues.textContent = `Failed to create: ${errorMsg}`;
                            // If duplicate, show helpful message
                            if (errorMsg.includes('duplicate') || errorMsg.includes('already exists')) {
                                detailsTextResidues.textContent = 'A custom binding site with these exact residues already exists. Please select different residues.';
                            }
                        }
                        submitBtn.classList.remove('disabled');
                        return;
                    }
                    // Clear residue selection
                    wrapper.clearResidueSelection();
                    window.currentBindingSiteID = null;
                    window.currentCustomBindingSiteID = result.customBindingSite.id;
                    window.bindingSiteSubmitted = true;
                    setBindingSiteState(receptorID, { bindingSiteID: null, customBindingSiteID: result.customBindingSite.id, submitted: true });
                    (_f = (_e = window).updateScreeningLaunchText) === null || _f === void 0 ? void 0 : _f.call(_e);
                    // Switch to custom mode and reload list, then select the newly created binding site
                    modeSelect.value = 'custom';
                    yield updateModeUI('custom');
                    // Reload list with the newly created binding site selected
                    yield loadCustomBindingSitesList(result.customBindingSite.id);
                    // Show success message
                    if (detailsTextResidues) {
                        detailsTextResidues.textContent = `Custom Binding Site ${result.customBindingSite.id} created!`;
                    }
                    // Update peptide details to show docking availability
                    if (typeof window.updatePeptideDetailsGlobal === 'function') {
                        window.updatePeptideDetailsGlobal();
                    }
                    console.log(`[DEBUG] Custom binding site ${result.customBindingSite.id} created and submitted for receptor ${receptorID}`);
                    // Stay on custom binding sites list (don't return to receptor tab)
                }
                catch (error) {
                    console.error('Error creating custom binding site:', error);
                    if (detailsTextResidues) {
                        detailsTextResidues.textContent = 'Failed to create custom binding site';
                    }
                    submitBtn.classList.remove('disabled');
                }
            }
        }));
        // Note: Mol* binding site events are bound in openBindingSiteInterface after DOM update
    }
    // Function to restore original receptor tab content
    function restoreReceptorTab() {
        var _a, _b;
        // Show the receptor list and hide the binding site / screening interfaces
        const receptorList = document.getElementById('wizard-step-1');
        const bindingSiteInterface = document.getElementById('binding-site-interface');
        const screeningInterface = document.getElementById('screening-interface');
        const formTitle = document.querySelector('.form-title');
        if (receptorList) {
            receptorList.style.display = 'block';
        }
        // Always hide the binding site interface when restoring the receptor tab
        if (bindingSiteInterface) {
            bindingSiteInterface.style.display = 'none';
        }
        if (screeningInterface) {
            screeningInterface.style.display = 'none';
        }
        if (formTitle) {
            formTitle.style.display = 'block';
        }
        // If we have a current receptor, load it in Mol*
        if (window.currentReceptorID && window.currentPdbID) {
            // Check if binding site interface is open (even if hidden)
            const bindingSiteInterface = document.getElementById('binding-site-interface');
            const isBindingSiteOpen = bindingSiteInterface && bindingSiteInterface.style.display !== 'none';
            if (isBindingSiteOpen && window.receptorWithPocketsPath) {
                // Load receptor with binding sites if interface is open
                console.log(`[DEBUG] Restoring receptor with pockets: ${window.receptorWithPocketsPath}`);
                (_a = window.loadPdbInMolstar) === null || _a === void 0 ? void 0 : _a.call(window, 'receptor_with_pockets', window.receptorWithPocketsPath);
            }
            else {
                // Load receptor without binding sites
                const receptorPath = `/frontend/userSessions/dataBase/receptor-${String(window.currentReceptorID).padStart(5, '0')}/rec.pdb`;
                (_b = window.loadPdbInMolstar) === null || _b === void 0 ? void 0 : _b.call(window, window.currentPdbID, receptorPath);
            }
            // Select the receptor element in the list (it should still be there)
            const receptorElement = document.querySelector(`[data-receptor-id="${window.currentReceptorID}"]`);
            if (receptorElement) {
                selectReceptorElement(receptorElement);
                console.log(`[DEBUG] Restored selection for receptor ID: ${window.currentReceptorID}`);
            }
            else {
                console.warn(`[DEBUG] Receptor element not found for ID: ${window.currentReceptorID} after restore`);
            }
        }
    }
    function createReceptorElement(item) {
        const el = document.createElement('div');
        el.className = 'receptor-item';
        el.setAttribute('data-receptor-id', String(item.receptorID));
        console.log(`[DEBUG] Creating receptor element for ${item.pdbID} with status: ${item.status}`);
        // Use the actual status from DynamoDB
        let statusLabel = '⏳ Processing...';
        let statusCls = 'processing';
        if (item.status === 'completed') {
            statusLabel = '✅ Ready';
            statusCls = 'ready';
        }
        else if (item.status === 'failed' || item.status === 'job_submission_failed') {
            statusLabel = '❌ Error';
            statusCls = 'error';
        }
        else if (item.status === 'in_progress') {
            statusLabel = '⏳ Processing...';
            statusCls = 'processing';
        }
        else if (item.status === 'pending') {
            statusLabel = '⏳ Pending...';
            statusCls = 'pending';
        }
        else if (item.status === 'cancelled') {
            statusLabel = '🚫 Cancelled';
            statusCls = 'error';
        }
        // Show stop button only when job is in progress or pending
        const showStopBtn = item.status === 'in_progress' || item.status === 'pending';
        // Show check button only when job is pending or in_progress
        const showCheckBtn = item.status === 'pending' || item.status === 'in_progress';
        el.innerHTML = `
      <div class="receptor-item-header">
        <div class="receptor-left">
          <span class="peptide-number">0</span>
          <span class="peptide-status ${statusCls}">${statusLabel}</span>
          <span class="chip chip-rec">Receptor: ${item.pdbID || 'Unknown'}</span>
          <span class="status-message-receptor-${item.receptorID}" style="display: none; color: #e74c3c; font-size: 11px; margin-left: 8px;"></span>
        </div>
        <div class="peptide-controls">
          <button class="btn-check btn-icon ${!showCheckBtn ? 'btn-hidden' : ''}" title="Refresh status">
            <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
              <path d="M3 21v-5h5"/>
            </svg>
          </button>
          <button class="btn-restart btn-icon ${item.status !== 'cancelled' ? 'btn-hidden' : ''}" title="Restart receptor preparation">
            <svg class="restart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 4v6h6"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
          </button>
          <button class="btn-stop btn-icon ${!showStopBtn ? 'btn-hidden' : ''}" title="Stop receptor preparation">
            <svg class="stop-icon" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" fill="currentColor"/>
              <rect x="8" y="8" width="8" height="8" fill="white" rx="1"/>
            </svg>
          </button>
          <button class="btn-binding-site btn-icon btn-hidden" title="Select binding site">
            <svg class="binding-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
          ${hasScreeningAccess() ? `<button class="btn-screening btn-icon btn-hidden" title="Screenings">
            <svg class="screening-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="10" cy="10" r="7"/>
              <line x1="15" y1="15" x2="23" y2="23"/>
            </svg>
          </button>` : ''}
          <button class="btn-delete btn-icon btn-visible" title="Remove receptor">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    `;
        const refreshBtn = el.querySelector('.btn-check');
        const restartBtn = el.querySelector('.btn-restart');
        const stopBtn = el.querySelector('.btn-stop');
        const bindingSiteBtn = el.querySelector('.btn-binding-site');
        const screeningBtn = el.querySelector('.btn-screening');
        const deleteBtn = el.querySelector('.btn-delete');
        // Check button: only show during processing/pending
        if (item.status === 'pending' || item.status === 'in_progress') {
            refreshBtn.classList.remove('btn-hidden');
            refreshBtn.classList.add('btn-visible');
        }
        else {
            refreshBtn.classList.remove('btn-visible');
            refreshBtn.classList.add('btn-hidden');
        }
        // Restart button: only show when cancelled
        if (item.status === 'cancelled' && restartBtn) {
            restartBtn.classList.remove('btn-hidden');
            restartBtn.classList.add('btn-visible');
            console.log(`[DEBUG] Restart button should be visible for cancelled receptor ${item.pdbID}`);
        }
        else if (restartBtn) {
            restartBtn.classList.remove('btn-visible');
            restartBtn.classList.add('btn-hidden');
        }
        // Show stop button only when job is in progress or pending
        if (showStopBtn && stopBtn) {
            stopBtn.classList.remove('btn-hidden');
            stopBtn.classList.add('btn-visible');
        }
        else if (stopBtn) {
            stopBtn.classList.remove('btn-visible');
            stopBtn.classList.add('btn-hidden');
        }
        else {
        }
        // Show binding site and screening buttons only when receptor is completed
        if (item.status === 'completed') {
            console.log(`[DEBUG] Showing binding site and screening buttons for completed receptor ${item.pdbID}`);
            bindingSiteBtn.classList.remove('btn-hidden');
            bindingSiteBtn.classList.add('btn-visible');
            bindingSiteBtn.disabled = false;
            bindingSiteBtn.title = 'Select binding site';
            // Screening button only exists when user has screening access
            if (screeningBtn) {
                screeningBtn.classList.remove('btn-hidden');
                screeningBtn.classList.add('btn-visible');
                screeningBtn.disabled = false;
                screeningBtn.title = 'Screenings';
            }
        }
        else {
            console.log(`[DEBUG] Hiding binding site and screening buttons for receptor ${item.pdbID} with status: ${item.status}`);
            bindingSiteBtn.classList.remove('btn-visible');
            bindingSiteBtn.classList.add('btn-hidden');
            if (screeningBtn) {
                screeningBtn.classList.remove('btn-visible');
                screeningBtn.classList.add('btn-hidden');
            }
        }
        refreshBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
            if (receptorID > 0) {
                refreshReceptor(receptorID);
            }
        });
        // Restart button: relaunch the receptor preparation
        if (restartBtn) {
            restartBtn.addEventListener('click', (ev) => __awaiter(this, void 0, void 0, function* () {
                ev.stopPropagation();
                ev.preventDefault();
                const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
                const pdbID = item.pdbID || '';
                if (receptorID > 0 && pdbID) {
                    yield restartReceptor(receptorID, pdbID, el);
                }
                else {
                    const showToast = window.showToast;
                    if (showToast) {
                        showToast('Cannot restart receptor: missing information. Please try again.', 'error', 4000);
                    }
                }
            }));
        }
        if (stopBtn) {
            console.log(`[DEBUG] Attaching stop button event listener for receptor ${item.pdbID}`);
            // Use both addEventListener and onclick to ensure it works
            const stopHandler = (ev) => __awaiter(this, void 0, void 0, function* () {
                ev.stopPropagation();
                ev.preventDefault();
                console.log('[DEBUG] ========== STOP BUTTON CLICKED ==========');
                console.log('[DEBUG] Event:', ev);
                console.log('[DEBUG] Target:', ev.target);
                console.log('[DEBUG] CurrentTarget:', ev.currentTarget);
                // Read receptorID from data attribute to ensure we have the latest value
                const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
                console.log('[DEBUG] ReceptorID from data attribute:', receptorID);
                if (receptorID > 0) {
                    console.log('[DEBUG] Calling stopReceptor with receptorID:', receptorID);
                    console.log('[DEBUG] stopReceptor function exists:', typeof stopReceptor);
                    // Call stopReceptor directly - it's in the same scope
                    try {
                        console.log('[DEBUG] About to call stopReceptor...');
                        yield stopReceptor(receptorID);
                        console.log('[DEBUG] stopReceptor completed');
                    }
                    catch (error) {
                        console.error('[DEBUG] ========== ERROR IN stopReceptor ==========');
                        console.error('[DEBUG] Error in stopReceptor:', error);
                        console.error('[DEBUG] Error stack:', error instanceof Error ? error.stack : 'No stack');
                    }
                }
                else {
                    console.warn('[DEBUG] Cannot stop receptor: invalid receptorID (0 or missing)');
                    const showToast = window.showToast;
                    if (showToast) {
                        showToast('Cannot stop receptor: receptor ID not yet available. Please wait a moment and try again.', 'warning', 4000);
                    }
                }
            });
            stopBtn.addEventListener('click', stopHandler, { capture: true });
            // Also set onclick as backup
            stopBtn.onclick = stopHandler;
        }
        else {
        }
        bindingSiteBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
            if (receptorID > 0) {
                openBindingSiteInterface(receptorID, item.pdbID || 'Unknown');
            }
        });
        // Screening button only exists if user has screening access
        if (screeningBtn) {
            screeningBtn.addEventListener('click', (ev) => {
                ev.stopPropagation();
                const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
                if (receptorID > 0) {
                    openScreeningInterface(receptorID, item.pdbID || 'Unknown');
                }
            });
        }
        deleteBtn.addEventListener('click', (ev) => __awaiter(this, void 0, void 0, function* () {
            ev.stopPropagation();
            // Show confirmation modal
            const showConfirm = window.showConfirmationModal;
            if (!showConfirm) {
                console.error('showConfirmationModal not available');
                return;
            }
            // Read receptorID from data attribute to ensure we have the latest value
            const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
            if (receptorID <= 0) {
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Cannot delete receptor: receptor ID not available.', 'warning', 4000);
                }
                return;
            }
            const confirmed = yield showConfirm('Delete Receptor', `Are you sure you want to delete the receptor "${item.pdbID}"? It will be permanently removed from your receptor list and cannot be restored.`, 'Delete', 'Cancel');
            if (!confirmed) {
                return;
            }
            // Add to deletion queue if not already queued
            if (!deletionQueue.includes(receptorID)) {
                deletionQueue.push(receptorID);
            }
            // Start processing queue if not already processing
            if (!isDeletingReceptor) {
                processDeletionQueue();
            }
        }));
        el.addEventListener('click', () => {
            const statusSpan = el.querySelector('.peptide-status');
            const isReady = Boolean(statusSpan && statusSpan.classList.contains('ready'));
            if (!isReady)
                return; // block selecting non-ready receptors
            selectReceptorElement(el);
        });
        // Update delete button visibility based on initial state
        updateReceptorDeleteButtons();
        return el;
    }
    function refreshReceptor(receptorID) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const res = yield checkReceptor(receptorID);
                const item = listContainer.querySelector(`.receptor-item[data-receptor-id="${receptorID}"]`);
                if (!item)
                    return;
                const statusSpan = item.querySelector('.peptide-status');
                // Get previous status to detect changes
                // Check if this is the first refresh after launch (element has data-just-launched attribute)
                const isFirstRefresh = item.hasAttribute('data-just-launched');
                const previousStatus = (statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.classList.contains('ready')) ? 'completed' :
                    (statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.classList.contains('error')) && ((_a = statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.textContent) === null || _a === void 0 ? void 0 : _a.includes('Cancelled')) ? 'cancelled' :
                        (statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.classList.contains('error')) ? 'failed' :
                            ((statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.classList.contains('processing')) || (statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.classList.contains('pending'))) ? 'processing' : 'processing';
                if (res.status === 'completed' && res.pdbID && res.receptorPath) {
                    if (statusSpan) {
                        statusSpan.textContent = '✅ Ready';
                        statusSpan.className = 'peptide-status ready';
                    }
                    // Hide check when ready
                    const checkBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-check');
                    if (checkBtn) {
                        checkBtn.classList.remove('btn-visible');
                        checkBtn.classList.add('btn-hidden');
                    }
                    // Hide stop button when ready
                    const stopBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-stop');
                    if (stopBtn) {
                        stopBtn.classList.remove('btn-visible');
                        stopBtn.classList.add('btn-hidden');
                    }
                    // Show binding site button when ready
                    const bindingSiteBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-binding-site');
                    const screeningBtnRef = item === null || item === void 0 ? void 0 : item.querySelector('.btn-screening'); // Only exists if user has screening access
                    if (bindingSiteBtn) {
                        bindingSiteBtn.classList.remove('btn-hidden');
                        bindingSiteBtn.classList.add('btn-visible');
                    }
                    // Show screening button only if user has access
                    if (screeningBtnRef) {
                        screeningBtnRef.classList.remove('btn-hidden');
                        screeningBtnRef.classList.add('btn-visible');
                    }
                    // Load receptor structure
                    (_b = window.loadPdbInMolstar) === null || _b === void 0 ? void 0 : _b.call(window, res.pdbID, res.receptorPath);
                    // Auto-select the receptor when it becomes completed
                    selectReceptorElement(item);
                    // Check if this is a different receptor than currently selected
                    const isDifferentReceptor = window.currentReceptorID !== receptorID;
                    if (isDifferentReceptor) {
                        // Reset binding sites when switching to different receptor
                        // step2Btn logic removed - no longer needed with new tab structure
                    }
                    window.currentPdbID = res.pdbID;
                    window.currentReceptorID = res.receptorID || receptorID;
                    // step2Btn logic removed - no longer needed with new tab structure
                    updatePeptideDetailsGlobal();
                    // show delete buttons again if no processing remains
                    updateReceptorDeleteButtons();
                    // Show toast if status changed to completed (but not during restart or stop)
                    if (previousStatus !== 'completed' && !item.hasAttribute('data-restarting') && !item.hasAttribute('data-stopping')) {
                        const showToast = window.showToast;
                        if (showToast) {
                            // Only show toast if not during bulk refresh
                            if (!item.hasAttribute('data-refreshing-all')) {
                                showToast(`Receptor ${res.pdbID} is now ready.`, 'success', 4000);
                            }
                        }
                    }
                }
                else if (res.status === 'failed' || res.status === 'job_submission_failed') {
                    if (statusSpan) {
                        statusSpan.textContent = '❌ Error';
                        statusSpan.className = 'peptide-status error';
                    }
                    // Hide check on error
                    const checkBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-check');
                    if (checkBtn) {
                        checkBtn.classList.remove('btn-visible');
                        checkBtn.classList.add('btn-hidden');
                    }
                    // Hide restart button on error
                    const restartBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-restart');
                    if (restartBtn) {
                        restartBtn.classList.remove('btn-visible');
                        restartBtn.classList.add('btn-hidden');
                    }
                    // Hide stop button on error
                    const stopBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-stop');
                    if (stopBtn) {
                        stopBtn.classList.remove('btn-visible');
                        stopBtn.classList.add('btn-hidden');
                    }
                    // Hide binding site button on error
                    const bindingSiteBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-binding-site');
                    const screeningBtnRef = item === null || item === void 0 ? void 0 : item.querySelector('.btn-screening'); // May not exist
                    if (bindingSiteBtn) {
                        bindingSiteBtn.classList.remove('btn-visible');
                        bindingSiteBtn.classList.add('btn-hidden');
                    }
                    if (screeningBtnRef) {
                        screeningBtnRef.classList.remove('btn-visible');
                        screeningBtnRef.classList.add('btn-hidden');
                    }
                    // Display error message if available
                    const errorMsgSpan = item.querySelector(`.status-message-receptor-${receptorID}`);
                    if (errorMsgSpan && res.errorMessage) {
                        errorMsgSpan.textContent = res.errorMessage;
                        errorMsgSpan.style.display = 'inline';
                    }
                    updateReceptorDeleteButtons();
                    // Show toast if status changed to failed (but not during restart or stop)
                    if (previousStatus !== 'failed' && !item.hasAttribute('data-restarting') && !item.hasAttribute('data-stopping')) {
                        const showToast = window.showToast;
                        if (showToast) {
                            // Only show toast if not during bulk refresh
                            if (!item.hasAttribute('data-refreshing-all')) {
                                showToast(`Receptor preparation has failed.`, 'error', 4000);
                            }
                        }
                    }
                }
                else if (res.status && res.status === 'cancelled') {
                    if (statusSpan) {
                        statusSpan.textContent = '🚫 Cancelled';
                        statusSpan.className = 'peptide-status error';
                    }
                    // Hide check and stop buttons when cancelled
                    const checkBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-check');
                    if (checkBtn) {
                        checkBtn.classList.remove('btn-visible');
                        checkBtn.classList.add('btn-hidden');
                    }
                    const stopBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-stop');
                    if (stopBtn) {
                        stopBtn.classList.remove('btn-visible');
                        stopBtn.classList.add('btn-hidden');
                    }
                    // Show restart button when cancelled
                    const restartBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-restart');
                    if (restartBtn) {
                        restartBtn.classList.remove('btn-hidden');
                        restartBtn.classList.add('btn-visible');
                    }
                    // Hide binding site button when cancelled
                    const bindingSiteBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-binding-site');
                    const screeningBtnRef = item === null || item === void 0 ? void 0 : item.querySelector('.btn-screening'); // May not exist
                    if (bindingSiteBtn) {
                        bindingSiteBtn.classList.remove('btn-visible');
                        bindingSiteBtn.classList.add('btn-hidden');
                    }
                    if (screeningBtnRef) {
                        screeningBtnRef.classList.remove('btn-visible');
                        screeningBtnRef.classList.add('btn-hidden');
                    }
                    updateReceptorDeleteButtons();
                }
                else {
                    if (statusSpan) {
                        statusSpan.textContent = '⏳ Processing...';
                        statusSpan.className = 'peptide-status processing';
                    }
                    // Show check while processing
                    const btn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-check');
                    if (btn) {
                        btn.classList.remove('btn-hidden');
                        btn.classList.add('btn-visible');
                    }
                    // Hide restart button when not cancelled/processing
                    const restartBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-restart');
                    if (restartBtn) {
                        restartBtn.classList.remove('btn-visible');
                        restartBtn.classList.add('btn-hidden');
                    }
                    // Show stop button while processing
                    const stopBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-stop');
                    if (stopBtn) {
                        stopBtn.classList.remove('btn-hidden');
                        stopBtn.classList.add('btn-visible');
                    }
                    // Hide binding site button while processing
                    const bindingSiteBtn = item === null || item === void 0 ? void 0 : item.querySelector('.btn-binding-site');
                    const screeningBtnRef = item === null || item === void 0 ? void 0 : item.querySelector('.btn-screening'); // May not exist
                    if (bindingSiteBtn) {
                        bindingSiteBtn.classList.remove('btn-visible');
                        bindingSiteBtn.classList.add('btn-hidden');
                    }
                    if (screeningBtnRef) {
                        screeningBtnRef.classList.remove('btn-visible');
                        screeningBtnRef.classList.add('btn-hidden');
                    }
                    updateReceptorDeleteButtons();
                    // Show toast if still processing (but not on first refresh after launch or restart or stop, or during bulk refresh)
                    const currentStatus = res.status;
                    if ((currentStatus === 'in_progress' || currentStatus === 'pending') && !isFirstRefresh && !item.hasAttribute('data-restarting') && !item.hasAttribute('data-stopping') && !item.hasAttribute('data-refreshing-all')) {
                        const showToast = window.showToast;
                        if (showToast) {
                            showToast('Receptor preparation is still in progress.', 'info', 4000);
                        }
                    }
                }
            }
            catch (e) {
                console.error('Error refreshing receptor:', e);
            }
        });
    }
    function restartReceptor(receptorID, pdbID, element) {
        return __awaiter(this, void 0, void 0, function* () {
            // Hide restart button immediately and update UI
            const restartBtn = element.querySelector('.btn-restart');
            if (restartBtn) {
                restartBtn.classList.remove('btn-visible');
                restartBtn.classList.add('btn-hidden');
            }
            // Show refresh and stop buttons immediately
            const refreshBtn = element.querySelector('.btn-check');
            if (refreshBtn) {
                refreshBtn.classList.remove('btn-hidden');
                refreshBtn.classList.add('btn-visible');
            }
            const stopBtn = element.querySelector('.btn-stop');
            if (stopBtn) {
                stopBtn.classList.remove('btn-hidden');
                stopBtn.classList.add('btn-visible');
            }
            // Update status immediately
            const statusSpan = element.querySelector('.peptide-status');
            if (statusSpan) {
                statusSpan.textContent = '⏳ Pending...';
                statusSpan.className = 'peptide-status pending';
            }
            // Mark element as just restarted to prevent "still in progress" toast on first refresh
            element.setAttribute('data-just-launched', 'true');
            // Hide delete button immediately (item is now processing again)
            updateReceptorDeleteButtons();
            const showToast = window.showToast;
            try {
                // Relaunch the receptor preparation with the same PDB ID
                // This will create a new entry in DB with status pending/in_progress
                const result = yield prepareReceptor(pdbID);
                if (result.submitted && result.receptorID && result.receptorID > 0) {
                    // Update the element with the new receptor ID
                    element.setAttribute('data-receptor-id', String(result.receptorID));
                    // Mark as restarting to prevent toasts from refreshReceptor
                    element.setAttribute('data-restarting', 'true');
                    // Refresh the receptor status to update UI
                    yield refreshReceptor(result.receptorID);
                    // Remove flags after refresh
                    element.removeAttribute('data-just-launched');
                    element.removeAttribute('data-restarting');
                    // Show success toast (only one toast, replaces any previous)
                    if (showToast) {
                        showToast(`Receptor preparation restarted for receptor ${pdbID}.`, 'success', 4000);
                    }
                }
                else {
                    element.removeAttribute('data-just-launched');
                    if (showToast) {
                        showToast('Failed to restart receptor preparation. Please try again.', 'error', 4000);
                    }
                }
            }
            catch (error) {
                console.error('Error restarting receptor:', error);
                element.removeAttribute('data-just-launched');
                if (showToast) {
                    showToast('Error restarting receptor preparation. Please try again.', 'error', 4000);
                }
            }
        });
    }
    function stopReceptor(receptorID) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Show confirmation modal IMMEDIATELY (before checking status)
                const showConfirm = window.showConfirmationModal;
                if (!showConfirm) {
                    alert('Confirmation modal not available. Please refresh the page.');
                    return;
                }
                const confirmed = yield showConfirm('Stop Receptor Preparation', 'Are you sure you want to stop this receptor preparation? The process will be stopped and cannot be resumed.', 'Stop', 'Cancel');
                if (!confirmed) {
                    return;
                }
                // Send toast IMMEDIATELY after confirmation (like restart)
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Receptor preparation will be stopped.', 'info', 4000);
                }
                // Get the element to mark it as stopping (prevents toasts from refreshReceptor)
                const listContainer = ensureReceptorListContainer();
                const item = listContainer.querySelector(`.receptor-item[data-receptor-id="${receptorID}"]`);
                if (item) {
                    item.setAttribute('data-stopping', 'true');
                }
                // Check status from backend FIRST to see if job is already done (before calling stop API)
                const { checkReceptor } = yield import('../backendClient');
                const statusResult = yield checkReceptor(receptorID);
                // Check if the job is already completed, failed, or cancelled
                if (statusResult.status === 'completed') {
                    // Refresh to update UI (but no toast from refreshReceptor due to data-stopping)
                    yield refreshReceptor(receptorID);
                    // Remove flag and show our toast
                    if (item)
                        item.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('This receptor preparation has already completed successfully.', 'success', 4000);
                    }
                    return;
                }
                if (statusResult.status === 'failed' || statusResult.status === 'job_submission_failed') {
                    // Refresh to update UI (but no toast from refreshReceptor due to data-stopping)
                    yield refreshReceptor(receptorID);
                    // Remove flag and show our toast
                    if (item)
                        item.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('This receptor preparation has already failed.', 'error', 4000);
                    }
                    return;
                }
                if (statusResult.status === 'cancelled') {
                    // Refresh to update UI (but no toast from refreshReceptor due to data-stopping)
                    yield refreshReceptor(receptorID);
                    // Remove flag and show our toast
                    if (item)
                        item.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('This receptor preparation has already been cancelled.', 'warning', 4000);
                    }
                    return;
                }
                // If still in progress, proceed with stop
                const result = yield stopReceptorAPI(receptorID);
                if (result.success) {
                    // Refresh the receptor status to update UI (but no toast from refreshReceptor due to data-stopping)
                    yield refreshReceptor(receptorID);
                    // Remove flag and show our toast
                    if (item)
                        item.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('Receptor preparation has been cancelled successfully.', 'success', 4000);
                    }
                }
                else {
                    // Refresh status to get the actual current state (but no toast from refreshReceptor due to data-stopping)
                    yield refreshReceptor(receptorID);
                    // Remove flag and show our toast
                    if (item)
                        item.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('Failed to stop receptor preparation. Please try again.', 'error', 4000);
                    }
                    else {
                        alert('Failed to stop receptor preparation. Please try again.');
                    }
                }
            }
            catch (error) {
                console.error('Error stopping receptor:', error);
                // Remove flag in case of error
                const listContainer = ensureReceptorListContainer();
                const item = listContainer.querySelector(`.receptor-item[data-receptor-id="${receptorID}"]`);
                if (item) {
                    item.removeAttribute('data-stopping');
                }
                const showToast = window.showToast;
                if (showToast) {
                    showToast('An error occurred while stopping the receptor preparation. Please try again.', 'error', 4000);
                }
                else {
                    alert('An error occurred while stopping the receptor preparation. Please try again.');
                }
            }
        });
    }
    // No renumbering needed - items are sorted by creation date from backend
    // Keep UI dataset receptor IDs aligned with backend renumber after a deletion
    function selectReceptorElement(el) {
        var _a, _b;
        // Check if we're selecting a different receptor
        const receptorID = parseInt(el.getAttribute('data-receptor-id') || '0', 10);
        const isDifferentReceptor = window.currentReceptorID !== receptorID;
        // Get the list container (it might be recreated after restoreReceptorTab)
        const listContainer = document.getElementById('receptor-list-container');
        if (!listContainer) {
            console.warn('[DEBUG] List container not found in selectReceptorElement');
            return;
        }
        // Remove selection from all other receptors
        listContainer.querySelectorAll('.receptor-item').forEach(n => n.classList.remove('selected'));
        el.classList.add('selected');
        console.log(`[DEBUG] Selected receptor element with ID: ${receptorID}, classList:`, el.classList.toString());
        const chip = el.querySelector('.chip-rec');
        const pdb = ((_a = chip === null || chip === void 0 ? void 0 : chip.textContent) === null || _a === void 0 ? void 0 : _a.replace('Receptor: ', '').trim()) || '';
        const statusSpan = el.querySelector('.peptide-status');
        const isReady = Boolean(statusSpan && statusSpan.classList.contains('ready'));
        // Load binding site state for the selected receptor (per-receptor)
        const state = getBindingSiteState(receptorID);
        window.currentBindingSiteID = state.bindingSiteID;
        window.currentCustomBindingSiteID = state.customBindingSiteID;
        window.bindingSiteSubmitted = state.submitted;
        updatePeptideDetailsGlobal();
        // Load base receptor only if ready to avoid 404s
        if (isReady) {
            // Get current user from localStorage
            const currentUser = localStorage.getItem('currentUser');
            if (currentUser) {
                const userData = JSON.parse(currentUser);
                const username = userData.username;
                const sessionID = 1; // Use session 1 for Cognito users
                const baseReceptorPath = `/frontend/userSessions/dataBase/receptor-${String(receptorID).padStart(5, '0')}/rec.pdb`;
                (_b = window.loadPdbInMolstar) === null || _b === void 0 ? void 0 : _b.call(window, pdb || `receptor-${receptorID}`, baseReceptorPath);
                // Track current receptor selection globally for tab restore
                window.currentReceptorID = receptorID;
            }
            window.currentPdbID = pdb || window.currentPdbID;
        }
        // Always set current IDs even if not ready (for state), but only viewer loads when ready above
        window.currentReceptorID = receptorID;
        window.currentPdbID = pdb || window.currentPdbID;
        // step2Btn logic removed - no longer needed with new tab structure
        updatePeptideDetailsGlobal();
    }
    function updateReceptorDeleteButtons() {
        const items = Array.from(listContainer.querySelectorAll('.receptor-item'));
        // Check if any receptor is processing
        const anyProcessing = items.some(it => {
            const st = it.querySelector('.peptide-status');
            return st && (st.classList.contains('processing') || st.classList.contains('pending'));
        });
        // If we're currently deleting, hide ALL delete buttons
        if (isDeletingReceptor) {
            items.forEach(it => {
                const del = it.querySelector('.btn-delete');
                if (del) {
                    del.classList.remove('btn-visible');
                    del.classList.add('btn-hidden');
                    del.disabled = true;
                }
            });
            return;
        }
        // Check docking and screening state (receptors with dockings or screenings cannot be deleted)
        try {
            Promise.all([getSessionDockings(), getSessionScreenings()]).then(([d, screeningsData]) => {
                const dockings = (d === null || d === void 0 ? void 0 : d.dockings) || [];
                const screenings = (screeningsData === null || screeningsData === void 0 ? void 0 : screeningsData.screenings) || [];
                const anyDockingProcessing = dockings.some((dk) => dk && (dk.status === 'pending' || dk.status === 'in_progress'));
                const referencedReceptors = new Set();
                dockings.forEach((dk) => {
                    if (dk && typeof dk.receptorID === 'number')
                        referencedReceptors.add(dk.receptorID);
                });
                screenings.forEach((s) => {
                    var _a;
                    if (s && (typeof s.receptorID === 'number' || typeof s.receptorId === 'number')) {
                        referencedReceptors.add(Number((_a = s.receptorID) !== null && _a !== void 0 ? _a : s.receptorId));
                    }
                });
                if (anyDockingProcessing) {
                    // When any docking is processing, hide delete buttons for receptors involved in dockings/screenings
                    items.forEach(it => {
                        var _a;
                        const del = it.querySelector('.btn-delete');
                        if (!del)
                            return;
                        const id = parseInt(it.getAttribute('data-receptor-id') || '0', 10);
                        const isReferenced = referencedReceptors.has(id);
                        const statusSpan = it.querySelector('.peptide-status');
                        const isProcessing = statusSpan && (statusSpan.classList.contains('processing') || statusSpan.classList.contains('pending'));
                        if ((isProcessing && !((_a = statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.textContent) === null || _a === void 0 ? void 0 : _a.includes('Cancelled'))) || isReferenced) {
                            del.classList.remove('btn-visible');
                            del.classList.add('btn-hidden');
                            del.disabled = true;
                        }
                        else {
                            del.classList.remove('btn-hidden');
                            del.classList.add('btn-visible');
                            del.disabled = false;
                        }
                    });
                }
                else {
                    // Hide delete for receptors involved in any docking or screening (any status)
                    items.forEach(it => {
                        var _a;
                        const del = it.querySelector('.btn-delete');
                        if (!del)
                            return;
                        const id = parseInt(it.getAttribute('data-receptor-id') || '0', 10);
                        const isReferenced = referencedReceptors.has(id);
                        const statusSpan = it.querySelector('.peptide-status');
                        const isProcessing = statusSpan && (statusSpan.classList.contains('processing') || statusSpan.classList.contains('pending'));
                        if ((isProcessing && !((_a = statusSpan === null || statusSpan === void 0 ? void 0 : statusSpan.textContent) === null || _a === void 0 ? void 0 : _a.includes('Cancelled'))) || isReferenced) {
                            del.classList.remove('btn-visible');
                            del.classList.add('btn-hidden');
                            del.disabled = true;
                        }
                        else {
                            del.classList.remove('btn-hidden');
                            del.classList.add('btn-visible');
                            del.disabled = false;
                        }
                    });
                }
            }).catch(() => {
                // Fallback: show deletes when docking fetch failed
                // BUT only if no receptors are processing
                if (!anyProcessing) {
                    items.forEach(it => {
                        const del = it.querySelector('.btn-delete');
                        if (del) {
                            del.classList.remove('btn-hidden');
                            del.classList.add('btn-visible');
                            del.disabled = false;
                        }
                    });
                }
            });
        }
        catch (_a) {
            // Fallback: show deletes when error occurs
            // BUT only if no receptors are processing
            if (!anyProcessing) {
                items.forEach(it => {
                    const del = it.querySelector('.btn-delete');
                    if (del) {
                        del.classList.remove('btn-hidden');
                        del.classList.add('btn-visible');
                        del.disabled = false;
                    }
                });
            }
        }
    }
    // Update receptor delete buttons whenever dockings change
    window.addEventListener('dockings-updated', () => updateReceptorDeleteButtons());
    window.addEventListener('dockings-state', (e) => {
        const detail = (e === null || e === void 0 ? void 0 : e.detail) || {};
        const items = Array.from(listContainer.querySelectorAll('.receptor-item'));
        if (detail.anyProcessing) {
            items.forEach(it => {
                const del = it.querySelector('.btn-delete');
                if (del) {
                    del.classList.remove('btn-visible');
                    del.classList.add('btn-hidden');
                }
            });
            return;
        }
        const referenced = new Set((detail.referencedReceptorIDs || []).filter((x) => typeof x === 'number'));
        items.forEach(it => {
            const del = it.querySelector('.btn-delete');
            if (!del)
                return;
            const id = parseInt(it.getAttribute('data-receptor-id') || '0', 10);
            if (referenced.has(id)) {
                del.classList.remove('btn-visible');
                del.classList.add('btn-hidden');
            }
            else {
                del.classList.remove('btn-hidden');
                del.classList.add('btn-visible');
            }
        });
    });
    // Also listen for dockings-updated to ensure delete buttons are updated
    window.addEventListener('dockings-updated', () => updateReceptorDeleteButtons());
}
// Export function at module level - simple wrapper like dockingForm
export function loadSessionReceptors() {
    // This will be called from main.tsx, but the actual loading is done by the local function
    // in initReceptorForm() which has access to all the necessary functions
    if (typeof window.loadSessionReceptors === 'function') {
        return window.loadSessionReceptors();
    }
    return Promise.resolve();
}
// Process deletion queue sequentially
function processDeletionQueue() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (isDeletingReceptor || deletionQueue.length === 0) {
            return;
        }
        isDeletingReceptor = true;
        if (globalUpdateReceptorDeleteButtons) {
            globalUpdateReceptorDeleteButtons();
        }
        while (deletionQueue.length > 0) {
            const receptorID = deletionQueue.shift();
            try {
                const res = yield deleteSessionReceptor(receptorID);
                if (!res.deleted) {
                    if (res.reason === 'blocked_by_docking') {
                        alert('Cannot delete this receptor because a docking references it. Remove related dockings first.');
                    }
                    else if (res.reason === 'blocked_by_screening') {
                        alert('Cannot delete this receptor because it has associated screenings. Delete the screenings first.');
                    }
                    else {
                        alert(res.message || 'Failed to delete receptor');
                    }
                    continue; // Skip to next item in queue
                }
                // Remove element from DOM after successful backend deletion
                const el = document.querySelector(`.receptor-item[data-receptor-id="${receptorID}"]`);
                if (el) {
                    el.remove();
                    // Reset globals if we deleted the current receptor
                    if (window.currentReceptorID === receptorID) {
                        window.currentReceptorID = null;
                        window.currentPdbID = null;
                        window.currentBindingSiteID = null;
                        updatePeptideDetailsGlobal();
                        // step2Btn logic removed - no longer needed with new tab structure
                        // Remove receptor from Mol* visualizer
                        if (window.removeFromMolstar) {
                            window.removeFromMolstar(window.currentPdbID || '');
                        }
                    }
                    // Binding site state is global, no need to clear per receptor
                    // Select the first remaining receptor (no need to reload entire list)
                    const listContainer = document.getElementById('receptor-list-container');
                    if (listContainer) {
                        const remainingReceptor = listContainer.querySelector('.receptor-item');
                        if (remainingReceptor) {
                            // Update global variables with the remaining receptor ID
                            const newReceptorID = parseInt(remainingReceptor.getAttribute('data-receptor-id') || '0', 10);
                            const chip = remainingReceptor.querySelector('.chip-rec');
                            const pdb = ((_a = chip === null || chip === void 0 ? void 0 : chip.textContent) === null || _a === void 0 ? void 0 : _a.replace('Receptor: ', '').trim()) || '';
                            window.currentReceptorID = newReceptorID;
                            window.currentPdbID = pdb;
                            // Click to select and load in Mol*
                            remainingReceptor.click();
                        }
                        else {
                            // No receptors left, clear selection
                            window.currentReceptorID = null;
                            window.currentPdbID = null;
                            window.currentBindingSiteID = null;
                            updatePeptideDetailsGlobal();
                            // resetBindingSiteForm removed - no longer needed
                        }
                        // Renumber remaining receptors after deletion
                        const remainingReceptors = listContainer.querySelectorAll('.receptor-item');
                        remainingReceptors.forEach((item, index) => {
                            const numberSpan = item.querySelector('.peptide-number');
                            if (numberSpan)
                                numberSpan.textContent = `${index + 1}`;
                        });
                        if (!listContainer.querySelector('.receptor-item')) {
                            listContainer.innerHTML = '';
                            listContainer.style.display = 'none';
                            // Clear all structures from Mol* visualizer when no receptors remain
                            if (window.clearMolstar) {
                                window.clearMolstar();
                            }
                        }
                    }
                }
            }
            catch (error) {
                console.error('Error deleting receptor:', error);
                alert('Error deleting receptor. Please try again.');
            }
        }
        // Reset deletion state and update delete buttons
        isDeletingReceptor = false;
        if (globalUpdateReceptorDeleteButtons) {
            globalUpdateReceptorDeleteButtons();
        }
    });
}
